import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  CalendarCheck,
  CalendarPlus,
  ChevronsUpDown,
  CalendarDays,
  CaseSensitive,
  Clock,
  Columns3,
  Columns3Cog,
  Hash,
  Link2,
  ToggleLeft,
  Download,
  EllipsisVertical,
  GripVertical,
  Eye,
  Funnel,
  Pencil,
  Plus,
  RotateCw,
  Search,
  Trash2,
  UserPen,
  UserRound,
  Upload,
  X,
  Archive,
  ArchiveRestore,
  type LucideIcon,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

import { readImage } from "@/components/avatar";
import { Button, IconButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { ConfirmDialog, Drawer, Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { menuItemClass, MenuSeparator } from "@/components/ui/menu";
import {
  BackArrow,
  backLinkClass,
  FilterChip,
  Page,
  PageHeader,
  Toolbar,
} from "@/components/ui/page";
import { PAGER_BARE, PAGER_TIGHT, Pager } from "@/components/ui/pager";
import { SearchInput } from "@/components/ui/search-input";
import { Spinner } from "@/components/ui/spinner";
import { Loading, type LoadingAction, LoadingNote } from "@/components/ui/loading";
import { useDelayed } from "@/hooks/use-delayed";
import { Skeleton, SkeletonLines } from "@/components/ui/skeleton";
import {
  Checkbox,
  Field,
  Form,
  Input,
  Textarea,
  Toggle,
  controlClass,
} from "@/components/ui/field";
import { NameInput } from "@/components/ui/name-input";
import { FkSelect, fetchCompact, labelsKey } from "@/components/ui/fk-select";
import { OptionSelect } from "@/components/ui/option-select";
import { api, errorMessage, ifMatch } from "@/services/api";
import { decodeRecordId, encodeRecordId } from "@/utils/record-token";
import { useLoadMore } from "@/hooks/use-load-more";
import { useNarrow } from "@/hooks/use-narrow";
import { useTableFloor } from "@/hooks/use-table-floor";
import { useTranslation } from "@/store/locale";
import {
  type ListParams,
  createResource,
  deleteResource,
  listCursor,
  listResource,
  queryResource,
  updateResource,
} from "@/utils/geo";
import { can, hiddenFrom, useCan } from "@/utils/permissions";
import { gateForPath } from "@/constants/nav-permissions";
import { profileQuery, queryKeys } from "@/services/queries";
import type { Profile } from "@/meta/types";

import { missingColumns, parseCsv } from "@/utils/csv";
import { downloadBlob, downloadText } from "@/utils/download";
import { cn } from "@/utils/utils";

/**
 * One list-and-edit page for every master-data resource.
 *
 * The API already does the work — search, filters, a sort allowlist, paging —
 * so this only has to carry state to the query string and lay the result out.
 * Configuration is data: columns, sortable fields, filters, and the form.
 */

export interface Option {
  value: string | number;
  label: string;
}

export interface FieldDef {
  name: string;
  label: string;
  type?:
    | "text"
    | "number"
    | "select"
    | "fk"
    | "date"
    | "boolean"
    | "image"
    | "file"
    | "textarea"
    | "email"
    | "tel";
  required?: boolean;
  /** Group heading in the form; fields with the same section sit under one heading. */
  section?: string;
  /** Not editable once the row exists. */
  lockOnEdit?: boolean;
  /**
   * A switch beside the label of a required field the person may genuinely not
   * have — a last name is the case: someone who goes by one name is not
   * someone whose name is half-typed. The string is the switch's own label.
   *
   * Switched **on** is "they have none": the field empties, greys out and
   * stops being required, and the save sends null for it. Switched off again
   * it takes an answer as before. The switch reads as the exception, so it
   * starts off — on only for a row already on record with the field empty.
   */
  absentToggle?: string;
  disabled?: boolean;
  /**
   * Shown, greyed, and never sent.
   *
   * `disabled` greys a field out; this also keeps it out of the body, and the
   * two are not the same thing. The Basic Information card is where the
   * difference matters: an Associate sees the member's name above the address
   * they are correcting, because an address form with no name on it is a form
   * you cannot check yourself against — but the three name fields are not
   * theirs to change, and merely disabling the inputs would still have sent
   * them. That body is read as an edit of the name: `update_member` re-derives
   * `card_name` whenever a name field is present, and runs the name-and-
   * birthday duplicate check, so an unchanged name resent with a new street
   * would overwrite a card name somebody set by hand and could 409 on a legacy
   * duplicate that has nothing to do with this edit.
   *
   * `exclude_unset` on the API side is what makes the omission mean "leave it
   * alone" rather than "clear it".
   */
  showOnly?: boolean;
  /**
   * A name, which is stored the way it is printed on the card: in capitals.
   *
   * The enrolment form has uppercased the three name fields since it was
   * written; a dialog that took them as typed let "Dela Cruz" back in beside
   * the "DELA CRUZ" enrolment put there, which sort apart and read as two
   * people in an export. Text fields only.
   */
  uppercase?: boolean;
  step?: string;
  /** Numbers for a number field; `YYYY-MM-DD` for a date one. */
  min?: number | string;
  max?: number | string;
  /**
   * `file` only: what the picker offers, as an `accept` list, and the cap in
   * megabytes. Both are the server's rules said out loud — a file the API
   * would refuse is better turned away at the picker than uploaded, encoded
   * and rejected a minute later.
   */
  accept?: string;
  maxSizeMb?: number;
  /**
   * An employee number, with the name it belongs to filled into the field
   * named here.
   *
   * A note names three people, and legacy kept the number and the name of
   * each. The clerk has the number; typing the name as well is two chances to
   * disagree with it, so the name is looked up and the field holding it is
   * read-only.
   */
  employeeLookup?: string;
  /** `fk` only: the /compact endpoint; `parentParam` names the query key `dependsOn` fills. */
  endpoint?: string;
  parentParam?: string;
  /** `fk` only: query the picker always sends — a kind, a scope, a fixed filter. */
  params?: Record<string, string | number | boolean | undefined> | undefined;
  /**
   * `fk` only: a + beside the picker that adds a row to the related list and
   * picks it. The value a form needs does not always exist yet, and sending
   * someone to another screen to make it costs them the form they were filling.
   */
  create?: RelatedCreate;
  /** Static choices, or a loader keyed by the value of `dependsOn`. */
  options?: Option[];
  loadOptions?: (parent: string, signal?: AbortSignal) => Promise<Option[]>;
  dependsOn?: string;
}

/** The related list a `fk` field can add to without leaving the form. */
export interface RelatedCreate {
  singular: string;
  /** Where the row is POSTed: the list endpoint, not its `/compact`. */
  endpoint: string;
  fields: FieldDef[];
  /** The codename that may add one. Without it the + is hidden. */
  permission?: string;
}

export interface FilterDef {
  name: string;
  label: string;
  type: "number" | "select" | "fk" | "boolean" | "date" | "text";
  /** `fk` only: the /compact endpoint; `parentParam` names the query key `dependsOn` fills. */
  endpoint?: string;
  parentParam?: string;
  /** `fk` only: query sent with every page of options — what this field may pick from. */
  params?: Record<string, string | number | boolean>;
  options?: Option[];
  /** Static choices, or a loader; with `dependsOn`, keyed by that filter's value. */
  loadOptions?: (parent: string, signal?: AbortSignal) => Promise<Option[]>;
  dependsOn?: string;
}

/** What a bespoke detail view is given besides the row. */
export interface DetailContext {
  onClose: () => void;
  lookup: Lookup;
  onRefresh: () => void;
  busy: boolean;
  /** Absent when the screen or the person may not do it. */
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
}

/** Labels for ids, keyed by the filter whose options supplied them. */
export type Lookup = (filter: string, value: string | number | null) => ReactNode;

export interface ColumnDef<T> {
  key: string;
  label: string;
  /**
   * `visible` answers for any column key, including the inline ones — a cell
   * that draws a second value inside itself asks before drawing it.
   */
  render: (row: T, lookup: Lookup, visible: (key: string) => boolean) => ReactNode;
  /**
   * Drawn inside another column's cell rather than in one of its own — an
   * email under a name, a posting under a club. It gets no header and no
   * column, but it does get a switch in Customize Columns, which is the whole
   * point: a value nested in someone else's cell was the one thing there was
   * no way to turn off.
   */
  inline?: boolean;
  /** A field from the API's sort allowlist; absent means the column is not sortable. */
  sortKey?: string;
  /** Plain value for CSV export; defaults to the row's field named by `key`. */
  csv?: (row: T) => string | number | null | undefined;
  className?: string;
  /** The API fields this column reads, when they are not just `key`. */
  fields?: string[];
}

export interface RowAction {
  label: string;
  run: () => void;
  danger?: boolean;
  /** Drawn to the left of the label, as View, Edit and Delete carry theirs. */
  icon?: LucideIcon;
  /**
   * Below Delete rather than above it. For an action that is neither part of
   * the record's own lifecycle nor destructive — a request waiting on an
   * answer, say — and so reads last.
   */
  afterDelete?: boolean;
}

export interface ResourceConfig<T extends { id: number }> {
  title: string;
  /** One line under the title saying what this list is for. */
  description?: string;
  singular: string;
  endpoint: string;
  /** What a downloaded CSV is named, before the date. Defaults to the endpoint. */
  csvBase?: string;
  columns: ColumnDef<T>[];
  defaultSort?: { key: string; order: "asc" | "desc" };
  filters?: FilterDef[];
  fields: FieldDef[];
  toForm: (row: T) => Record<string, string>;
  /** Starting values for a new row — today's date, the viewer's club, and so on. */
  newDefaults?: (profile: Profile) => Record<string, string>;
  /** Extra controls before the refresh button. */
  toolbar?: ReactNode;
  /** A bespoke create/edit dialog, when the generic field form is not enough. */
  renderForm?: (editing: "new" | T, onClose: () => void, onSaved: () => void) => ReactNode;
  /**
   * Where `renderForm` is drawn. "panel" takes the page over, the way the role
   * editor does; "modal" leaves the list where it is and puts the form over it,
   * for a form short enough that the table behind is still worth seeing.
   */
  formVariant?: "panel" | "modal";
  /** The same choice for `renderView`. */
  viewVariant?: "panel" | "modal";
  /** A bespoke detail dialog. */
  /**
   * A bespoke detail view. It takes the page over exactly as the generic one
   * does, and is handed the same actions, so a screen with its own body still
   * gets the same back link, Refresh, Edit and Delete.
   */
  renderView?: (row: T, ctx: DetailContext) => ReactNode;
  /** Extra entries in the row menu, between Edit and Delete. `refresh` reloads the list. */
  rowActions?: (row: T, refresh: () => void) => RowAction[];
  /** No add, edit, delete or selection — a record of what happened. */
  readOnly?: boolean;
  /**
   * The backend model name — `province`, `user` — that the screen's codenames
   * are built from. With it, Add, Edit and Delete show only to a person who
   * holds `add__`, `change__` and `delete__` on it. Without it nothing is hidden.
   */
  model?: string;
  /**
   * The list scrolls on rather than paging, and shows no total: the API
   * answers `{items, next}` and is asked for the rows below `next` when the
   * table reaches its end. For a log too large to count, let alone page.
   */
  infinite?: boolean;
  /** Rows can be added and deleted but never changed — a record of a moment. */
  noEdit?: boolean;
  /**
   * Rows are never deleted. Not the same as `readOnly`: they can still be added
   * and changed. There is no delete route behind these either — a member and a
   * walk-in are records of something that happened.
   */
  noDelete?: boolean;
  /** Replaces the generic "Delete this {singular}?" line in the confirmation. */
  confirmDeleteMessage?: string;
  /**
   * "compact" strips the toolbar back to a small search box and a funnel,
   * with the screen's own actions on the right: no worded Filters button, no
   * Columns, and Refresh as an icon. For a screen whose actions are few
   * enough to name, where five worded buttons read as a second navigation.
   */
  toolbarVariant?: "full" | "compact";
  /**
   * How the toolbar is broken up. "inline" is one row that wraps when it runs
   * out of width; "split" gives the search box and the filter controls a line
   * of their own and puts every action on a second line against the right
   * edge.
   *
   * A different question from `toolbarVariant`, which is about how much of the
   * row is shown. This is about a row that shows everything and is simply
   * wide: a screen with seven actions wraps on most windows anyway, and where
   * it wraps then depends on the window rather than on what the buttons mean.
   */
  toolbarLayout?: "inline" | "split" | "stacked";
  /**
   * Refresh as a bare icon instead of a worded button.
   *
   * Separate from `toolbarVariant` because the two are different questions:
   * the variant is about the left of the row, this is about how crowded the
   * right of it is. A screen whose other actions are already worded keeps
   * Refresh worded beside them; one that is four actions wide does not.
   */
  iconRefresh?: boolean;
  /**
   * An icon that swaps the list between the live rows and the archived ones,
   * by setting one filter and clearing it again. Named rather than built in
   * because what "archived" means is the screen's: a status, a flag, a date.
   */
  archiveFilter?: { name: string; value: string; permission?: string };
  /**
   * The other half of `archiveFilter`: every row of the archive gets a Restore
   * action, which posts to `<endpoint>/<id>/restore`. Without it the archive is
   * a place rows go and do not come back from.
   */
  restore?: { permission?: string };
  /** No detail view: no row menu and no click-through on the id. With readOnly, no actions at all. */
  noView?: boolean;
  /** The column whose cell opens the detail view; defaults to `id`. */
  viewColumn?: string;
  /**
   * A double-click anywhere in a row opens the record too, not only the
   * view column's cell.
   *
   * The cell stays the way in that can be seen and reached by keyboard — this
   * is the shortcut beside it, for a list that is read across rather than down
   * and where the name is often scrolled off to the left by the time the
   * interesting column is in view. Off by default: on a screen whose rows are
   * meant to be selected or edited in place, a double-click is a word being
   * picked, not a record being opened.
   */
  openOnRowDoubleClick?: boolean;
  /** No row menu at all — the detail view carries the actions. */
  noRowMenu?: boolean;
  /** Recent searches under the box, and a Time Range that filters created_at. */
  searchHistory?: boolean;
  /**
   * Where recent searches live. Absent, the browser keeps them; given, the
   * server does, so they follow the account from one machine to the next.
   */
  searchHistoryStore?: {
    /**
     * The list, or only what has changed.
     *
     * `since` is the newest `updated_at` already held: the answer then carries
     * the handful of rows touched after it rather than the whole thousand.
     * Called without one when there is nothing held yet, and when a copy is
     * being reconciled — a delta can carry an edit but never an absence.
     */
    load: (since?: string, signal?: AbortSignal) => Promise<HistoryEntry[]>;
    /**
     * Files a committed term and answers with everything changed since `since`
     * — the term included — so keeping the held copy in step costs no request
     * of its own.
     */
    remember?: (term: string, since?: string) => Promise<HistoryEntry[]>;
    forget: (id: number) => Promise<unknown>;
  };
  /**
   * How much has to be typed before the search runs and is remembered. A short
   * prefix over a very large table is work with a useless answer; a small
   * catalogue leaves this at 0 and searches from the first letter.
   */
  searchMinLength?: number;
  /**
   * The longest term the API will take, as that endpoint's own schema declares
   * it — 200 for the members list, 30 for the member log. The box stops there
   * rather than let a long paste go out and come back a 422. Legacy capped
   * nothing at either end because its API refused nothing; here the API does,
   * so the box has to know the same number.
   */
  searchMaxLength?: number;
  /**
   * Typing suggests, Enter searches.
   *
   * The box matches the held history locally on every keystroke — instant, and
   * no request — while the list is left alone until a term is committed by
   * pressing Enter or picking a suggestion. For a table where a query per pause
   * in typing is real work, which is any table worth searching.
   */
  searchOnCommit?: boolean;
  /** Send the list's filters as a JSON body to `POST {endpoint}/query` rather than a query string. */
  listMethod?: "get" | "post";
  /**
   * Where the server stops counting. A list in the tens of millions is not
   * counted for every page; at or past this the footer says "10,000+".
   */
  countCap?: number;
  /** Text on the filter button instead of a bare icon. */
  filterLabel?: string;
  /** Beside the filter button: a control that reads or sets the filters — saved sets, say. */
  filterExtra?: (values: FilterValues, onApply: (values: FilterValues) => void) => ReactNode;
  /**
   * A strip above the toolbar that says what is in the list before anything is
   * asked of it. Pressing one of its figures is a filter, so it is handed the
   * same pair `filterExtra` gets rather than being a static header.
   */
  banner?: (values: FilterValues, onApply: (values: FilterValues) => void) => ReactNode;
  /** A bespoke filters dialog. */
  renderFilters?: (
    values: FilterValues,
    onApply: (values: FilterValues) => void,
    onClose: () => void,
  ) => ReactNode;
  /** Customize Columns: show/hide, remembered per browser. */
  columnPicker?: boolean;
  /**
   * Ticking rows, and what the toolbar may then do with them.
   *
   * The legacy table worked this way: a box on every row, a box in the header
   * for the page, and one button above that acts on what is ticked — delete on
   * the list, restore in the archive. Both are asked for once and applied row
   * by row, because some of the ticked rows will be refused and a person who
   * ticked forty should be told which three stayed.
   */
  bulk?: {
    /** Route below the resource that archives the ticked rows. */
    deletePath?: string;
    /** And the one that brings them back, used from the archive. */
    restorePath?: string;
    permission?: string;
  };
  /** Export the current page as CSV. */
  exportable?: boolean;
  /**
   * Have the server write the file instead, from the columns the person ticks.
   *
   * The legacy export worked this way — pick the columns, get every matching
   * row rather than the page on screen — and it is the only way to export a
   * list of forty thousand rows at all: the browser holds twenty-five of them.
   */
  exportServer?: { path?: string; permission?: string };
  /**
   * Import rows from a CSV, the other half of Export.
   *
   * The columns are what the file must carry and what the sample template is
   * written from — declared here rather than fetched, since the server's
   * template and this list are the same short fact and a round trip to learn
   * it is a round trip for nothing.
   */
  importable?: {
    /** The route that takes the file, under the resource: "import". */
    path?: string;
    /** The header the file must carry, as the template writes it. */
    columns: string[];
    /**
     * The sample file, served from `public/templates` and linked to directly.
     *
     * A real file at a real URL, rather than one built in the browser and
     * handed over by a scripted click: that click has no user gesture behind
     * it and is the first thing a browser drops — inside a modal, inside a
     * frame, behind a download policy — and it fails silently when it does.
     */
    template: string;
    /** What the download is saved as. */
    file: string;
    permission?: string;
  };
  /**
   * The screen takes the export over entirely.
   *
   * Handed the very parameters the list is asking with — so a download is the
   * same question without the page — together with the columns the table is
   * showing. A screen that supplies this owns every export from its button:
   * the file is the same file whether the list runs to twenty rows or twenty
   * million, which is what it was not while a short list was quietly written
   * from the browser and a long one from the server.
   *
   * Only Members supplies it. Without it the button behaves exactly as it
   * always has — `exportable` writes the loaded rows, `exportServer` opens the
   * server's own picker.
   */
  exportAll?: (choice: {
    params: ListParams;
    onScreen: number;
    total: number;
    /** The total as the pager writes it — "10,000+" when the count stopped at the cap. */
    totalLabel: string;
    /** The count stopped at the cap, so `total` is a floor and not a number of rows. */
    capped: boolean;
    /**
     * The columns the table is showing, in the order it shows them, each with
     * the API fields behind it.
     *
     * This is Customize Columns' answer and nothing else: what is on the
     * screen is what is in the file, so the choice is made in one place and
     * the download does not ask it a second time. An inline column counts —
     * it has a switch in that panel, and a value of its own to write — and a
     * column switched off is not in this list at all.
     */
    columns: { key: string; label: string; fields: string[] }[];
  }) => void;
  /** Extra rows in the overflow menu — where a screen's rarer actions belong. */
  menuExtra?: ReactNode;
  /** Two-column form. */
  formColumns?: 1 | 2;
  /** Columns hidden until the person turns them on. */
  hiddenByDefault?: string[];
  /** Ask the API for only the visible columns' fields; rows are fetched in full to view or edit. */
  projectFields?: boolean;
  /** Rendered under the field list in the detail dialog — a row's child records. */
  renderDetailExtra?: (row: T) => ReactNode;
  /**
   * Per-row overrides for Edit and Delete, where the rule is about the record
   * rather than the screen — a superuser account nobody else may touch. The
   * API refuses either way; this stops offering a button that cannot work.
   */
  rowCanEdit?: (row: T, profile: Profile | undefined) => boolean;
  rowCanDelete?: (row: T, profile: Profile | undefined) => boolean;
  /** Fields the detail view leaves out because `renderDetailExtra` shows them better. */
  detailHide?: string[];
  /**
   * Headings for the detail view, in order. A section either names the fields
   * under it or draws its own content — a picture, a club and its posting —
   * so the things that are not one value per row still sit where they belong
   * in the reading order rather than in a slab underneath everything.
   *
   * Anything unnamed falls into a last untitled group, and the created/updated
   * footnote stays after all of them: a record's own history reads last.
   */
  detailSections?: { title: string; fields?: string[]; render?: (row: T) => ReactNode }[];
  /** What a field is called in the detail view; anything unnamed is titled from its key. */
  detailLabels?: Record<string, string>;
  /** Replaces the Add button — a menu, a link, anything that starts a create. */
  addMenu?: ReactNode;
  /** Text on the add button; defaults to "Add {singular}" — it makes one row. */
  addLabel?: string;
  /** How the count line names many of them; defaults to "{singular}s". */
  plural?: string;
  /** A back arrow before the title. */
  backTo?: "/employees" | "/members" | "/yellow-page" | "/";
  searchPlaceholder?: string;
}

export const fmtDate = (value: string) => format(new Date(value), "MM/dd/yyyy hh:mm:ss a");

/** A stored search term is lower-cased; the dropdown shows it the way a name reads. */
const titleCase = (value: string) => value.replace(/\b\p{L}/gu, (c) => c.toUpperCase());

/** An identifier — employee, account, card number — set the way it is printed on the card. */
export function Mono({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={cn("text-subhead font-mono tracking-[0.01em] tabular-nums", className)}>
      {children}
    </span>
  );
}

/** Columns whose ids are accounts, resolved through the accounts list. */
const ACCOUNT_LOOKUPS = new Set(["created_by_id", "updated_by_id", "manager_id"]);

/** One remembered search, as the store holds it. */
export interface HistoryEntry {
  id?: number;
  term: string;
  updated_at?: string;
}

/** How many suggestions the box shows. The rest of the copy is what makes a
 *  prefix match find something worth showing. */
const SUGGESTIONS_SHOWN = 10;

/**
 * How long the held search history may go without a full read.
 *
 * Deletions are the only thing a delta cannot carry, and they are rare and not
 * urgent: a term somebody removed on another device staying in this browser's
 * suggestions for half an hour is a smaller cost than every refetch in that
 * half hour re-reading a thousand rows to notice it.
 */
const RECONCILE_EVERY_MS = 30 * 60_000;

/**
 * When each screen's history was last read whole, kept outside the component
 * so leaving the screen and coming back is a delta like any other refetch.
 */
const lastWholeRead = new Map<string, number>();

/** The Time Range as an ISO instant, for narrowing the held copy locally. */
const windowStart = (window: string): string | undefined => {
  const spans: Record<string, number> = {
    hour: 3_600_000,
    day: 86_400_000,
    week: 7 * 86_400_000,
    month: 28 * 86_400_000,
  };
  const span = spans[window];
  return span ? new Date(Date.now() - span).toISOString() : undefined;
};

/** What every `/compact` route caps `limit` at. Asking for more is a 422. */
const COMPACT_MAX = 200;

const ACCOUNTS = "/users/compact";

/** One shared empty map, so a miss does not make a new object every render. */
const EMPTY: Record<string, string> = {};

/**
 * What a filter holds: one value, or several where the endpoint takes a list.
 * The transport already carries both — `ListParams` and the body it becomes —
 * so a multi-select is a wider state here, not a wider protocol.
 */
export type FilterValues = Record<string, string | string[]>;

/** Whether a filter is actually narrowing anything. An empty list is not. */
export const isSet = (value: string | string[] | undefined): boolean =>
  Array.isArray(value) ? value.length > 0 : (value ?? "") !== "";

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be full or blocked; the page still works without it.
  }
}

/**
 * How far into a list a single jump may land, in rows. Past this the "last
 * page" affordances are not offered — see `canJumpToEnd`. Ten thousand rows is
 * an offset PostgreSQL walks without anyone noticing; eighty million is not.
 */
const DEEP_PAGE_ROWS = 10_000;

/**
 * What a downloaded list is called: `member_2026-09-06.csv`.
 *
 * One helper because the same list downloads two ways — the server streams the
 * whole filtered set, the browser writes the page it already has — and two
 * files of the same rows under two different names is a support question.
 * The base is not translated: a filename is not read by the person's locale.
 */
export const csvName = (base: string): string =>
  `${base}_${new Date().toISOString().slice(0, 10)}.csv`;

/** The visible columns of the current page as a CSV file, built in the browser. */
function exportCsv<T extends { id: number }>(base: string, cols: ColumnDef<T>[], rows: T[]) {
  const cell = (column: ColumnDef<T>, row: T) => {
    const raw = column.csv ? column.csv(row) : (row as Record<string, unknown>)[column.key];
    return `"${String(raw ?? "").replace(/"/g, '""')}"`;
  };
  const csv = [cols.map((c) => `"${c.label}"`).join(",")]
    .concat(rows.map((row) => cols.map((c) => cell(c, row)).join(",")))
    .join("\n");
  downloadText(csv, csvName(base));
}

/**
 * Where the toolbar stops fitting on one line. Below this the search keeps a
 * line of its own with the funnel, and the actions take the line under it —
 * a row wide enough to wrap by accident reads better broken on purpose.
 */
const TOOLBAR_STACK = 720;

/** Below this the pager drops its numbered window to three. */

/** And below this to none at all: the page it is on, and a way either side. */

export function ResourcePage<T extends { id: number }>({
  config,
}: {
  config: ResourceConfig<T>;
}) {
  const {
    title,
    description,
    singular,
    endpoint,
    csvBase = endpoint.replace(/^\//, ""),
    countCap,
    columns: declared,
    defaultSort,
    filters = [],
    fields,
    toForm,
    newDefaults,
    toolbar,
    renderForm,
    formVariant = "panel",
    renderView,
    viewVariant = "panel",
    rowActions,
    readOnly = false,
    model,
    noEdit: noEditDeclared = false,
    noDelete: noDeleteDeclared = false,
    confirmDeleteMessage,
    toolbarVariant = "full",
    toolbarLayout = "inline",
    iconRefresh = false,
    archiveFilter,
    restore,
    noView = false,
    viewColumn = "id",
    openOnRowDoubleClick = false,
    noRowMenu = false,
    backTo,
    searchPlaceholder,
    searchHistory = false,
    searchHistoryStore,
    searchMinLength = 0,
    searchMaxLength,
    searchOnCommit = false,
    listMethod = "get",
    filterLabel,
    renderFilters,
    filterExtra,
    banner,
    // Every list can choose its columns; the choice is remembered per browser
    // under `columns:<endpoint>`, so one screen's layout is not another's.
    columnPicker = true,
    projectFields = false,
    renderDetailExtra,
    detailHide,
    detailSections,
    detailLabels,
    rowCanEdit,
    rowCanDelete,
    addMenu,
    bulk,
    exportable = false,
    exportServer,
    importable,
    exportAll,
    menuExtra,
    formColumns = 1,
    // The audit columns are available everywhere and off everywhere until
    // someone asks for them; a screen can still name others.
    hiddenByDefault = [],
    addLabel,
    plural = `${singular}s`,
    infinite = false,
  } = config;
  const t = useTranslation();
  const queryClient = useQueryClient();
  const { data: profile } = useQuery(profileQuery());
  // What the person may do to this model; a screen with no model hides nothing.
  const canAdd = !model || can(profile, `add__${model}`);
  const noEdit = noEditDeclared || (!!model && !can(profile, `change__${model}`));
  const noDelete = noDeleteDeclared || (!!model && !can(profile, `delete__${model}`));

  const [search, setSearch] = useState("");
  // What the list is filtered by, which is not the same as what is in the box.
  const [deferredSearch, setDeferredSearch] = useState("");
  const longEnough = (value: string) => value.trim().length >= searchMinLength;
  // With `searchOnCommit` the list waits for `commit`. Without it, it follows
  // the typing after a pause — and below `searchMinLength` it asks nothing.
  //
  // Trimmed, like a committed term: with no minimum to clear, a lone space is
  // a term the box would otherwise send, and a term of whitespace matches no
  // member at all. An empty box is every row, not none of them.
  useEffect(() => {
    if (searchOnCommit) return;
    const id = setTimeout(
      () => setDeferredSearch(longEnough(search) ? search.trim() : ""),
      400,
    );
    return () => clearTimeout(id);
  }, [search, searchOnCommit]);
  // The suggestions do follow every keystroke, after a short pause of their own.
  const [typing, setTyping] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setTyping(search.trim()), 150);
    return () => clearTimeout(id);
  }, [search]);
  const [sort, setSort] = useState(defaultSort?.key ?? "name");
  const [order, setOrder] = useState<"asc" | "desc">(defaultSort?.order ?? "asc");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [showFilters, setShowFilters] = useState(false);
  const [editing, setEditing] = useState<"new" | T | null>(null);
  /**
   * The declared columns plus the two the API already sends on every read.
   *
   * "Who changed this last, and when" is a question of every list, and every
   * read schema answers it — it was simply never offered. Appended rather than
   * written into nine screens, off unless someone turns them on, and skipped
   * where a screen defines its own.
   */
  const columns = useMemo(() => {
    const audit: ColumnDef<T>[] = [
      {
        key: "updated_at",
        label: t("rp.updatedAt"),
        sortKey: "updated_at",
        render: (row) => fmtDate((row as Record<string, unknown>)["updated_at"] as string),
      },
      {
        key: "updated_by_id",
        label: t("rp.updatedBy"),
        render: (row, lookup) =>
          lookup("created_by_id", (row as Record<string, unknown>)["updated_by_id"] as number),
      },
    ];
    const named = new Set(declared.map((column) => column.key));
    // Nothing changes these rows — a log, or a record of a moment — so there is
    // no updated_at and no updated_by_id to show. Offering them put an "Updated
    // By" column of `undefined` in the table and two dead entries in Customize
    // Columns, beside a date the row does not have either.
    if (readOnly || noEdit) return declared;
    return [...declared, ...audit.filter((column) => !named.has(column.key))];
  }, [declared, t, readOnly, noEdit]);

  const [viewing, setViewing] = useState<T | null>(null);
  // A record is always read when it is opened, so there is a wait to show.
  const [opening, setOpening] = useState(false);
  const [refreshingOne, setRefreshingOne] = useState(false);
  /**
   * Which record is open, and which is being edited, live in the address bar.
   *
   * They were remembered for the tab, which could not tell a reload from
   * arriving fresh: opening province 4, going to Cities and coming back to
   * Provinces reopened province 4 — a page that would not let go. The URL has
   * no such trouble. It also gives the panel a Back that closes it and a link
   * that opens it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = Object.fromEntries(searchParams.entries()) as Record<string, unknown>;
  const viewToken = typeof urlSearch["view"] === "string" ? urlSearch["view"] : undefined;
  const editToken = typeof urlSearch["edit"] === "string" ? urlSearch["edit"] : undefined;

  const setParam = (key: "view" | "edit", id: number | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id === null) next.delete(key);
        else next.set(key, encodeRecordId(id));
        return next;
      },
      { replace: true },
    );

  // Only the address bar changes; the effect below decides where the record
  // comes from.
  const open = (row: T | null) => setParam("view", row?.id ?? null);

  /**
   * The rows the list is holding, for opening one without reading it again.
   *
   * A ref rather than a value because the list queries are declared well below
   * the effects that want them, and an effect body runs after the render that
   * fills this — so it is the current page by the time anything asks.
   */
  const rowsRef = useRef<T[]>([]);
  /**
   * The row already on screen, when it is the whole record.
   *
   * `projectFields` is the question: a screen that asks the API for only the
   * visible columns' fields has rows that are genuinely short, so viewing or
   * editing one has to read it — that is Members, and only Members. Everywhere
   * else the list and the detail answer with the same schema, and the read
   * returns a copy of what is already rendered behind the modal, having
   * charged a spinner for it.
   *
   * Editing from the row is safe on its own terms: rows carry `updated_at`, a
   * save sends it as `if_match`, and a save from a copy that has since moved
   * is refused with 412 rather than quietly overwriting. Freshness-on-open was
   * never what protected the write.
   */
  const rowOnHand = (id: number) =>
    projectFields ? undefined : rowsRef.current.find((row) => row.id === id);

  // The address bar is the truth: follow it, rather than only reading it once.
  // A token for a record that is gone, or one somebody typed, simply clears.
  useEffect(() => {
    const id = decodeRecordId(viewToken);
    if (id === null) {
      setViewing(null);
      return;
    }
    const known = rowOnHand(id);
    if (known) {
      // Already on screen. Opening it is not a reason to fetch it again, and
      // the modal comes up on the click rather than after a round trip.
      setViewing(known);
      return;
    }
    let cancelled = false;
    setOpening(true);
    void fetchOne(id)
      .then((row) => !cancelled && setViewing(row))
      .catch(() => !cancelled && setParam("view", null))
      .finally(() => !cancelled && setOpening(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewToken]);

  useEffect(() => {
    const id = decodeRecordId(editToken);
    if (id === null) {
      setEditing((current) => (current === "new" ? current : null));
      return;
    }
    const known = rowOnHand(id);
    if (known) {
      setEditing(known);
      return;
    }
    let cancelled = false;
    setOpening(true);
    void fetchOne(id)
      .then((row) => !cancelled && setEditing(row))
      .catch(() => !cancelled && setParam("edit", null))
      .finally(() => !cancelled && setOpening(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editToken]);
  const [searchFocus, setSearchFocus] = useState(false);
  const [pickingColumns, setPickingColumns] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  /**
   * The rows ticked, by id.
   *
   * Ids rather than rows: the answer is refetched under the selection all the
   * time — a poll, a save, a page — and a set of objects would hold copies of
   * rows that have since changed.
   */
  const [ticked, setTicked] = useState<Set<number>>(() => new Set());
  /** Whether the ticked rows are being asked about before anything happens. */
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  /** The rows the last bulk action would not move, which is a list to read. */
  const [refused, setRefused] = useState<BulkRefusal[] | null>(null);
  // A delete is not undoable, so it is confirmed; nothing else here is.
  const [deleting, setDeleting] = useState<number | null>(null);

  // Remembered per browser: recent searches, and which columns are on.
  const historyKey = `search-history:${endpoint}`;
  const columnsKey = `columns:${endpoint}`;
  const columnOrderKey = `column-order:${endpoint}`;
  const [localHistory, setLocalHistory] = useState<string[]>(
    () => readJson<string[]>(historyKey) ?? [],
  );
  // The Time Range beside the history narrows the history, not the list.
  const [historyWindow, setHistoryWindow] = useState("");
  // One fetch of the whole list per page open, held by react-query for the rest
  // of the session and refetched when the window regains focus. That refetch is
  // also what reconciles a term deleted on another device: a "what changed
  // since" answer can carry an edit but never an absence.
  const historyKeyQ = [endpoint, "search-history"];
  // Set when the next read has to be the whole list rather than a delta —
  // after a delete, which is the one change this screen makes that a delta
  // cannot describe.
  const forceWhole = useRef(false);
  const storedHistory = useQuery({
    queryKey: historyKeyQ,
    queryFn: async ({ signal }) => {
      const held = queryClient.getQueryData<HistoryEntry[]>(historyKeyQ) ?? [];
      // A delta once there is something to add it to. A thousand terms is
      // about ten kilobytes gzipped, which is worth fetching once and not
      // again every time this screen is opened.
      //
      // The whole list is read when there is nothing to add to, after a
      // delete, and otherwise no more often than `RECONCILE_EVERY_MS` — a
      // delta carries what changed, and a row that is gone changed by not
      // being there. That interval is the longest a term deleted on another
      // device can linger in these suggestions, traded against every refetch
      // in between costing a handful of rows instead of a thousand.
      //
      // Asked here rather than on a focus listener so it covers every reason a
      // read happens: the tab coming back, the five-minute staleness, an
      // invalidate, and the screen being opened again. The watermark is worked
      // out rather than read off the first row — the order the server returns
      // is not this screen's to depend on.
      const aged = Date.now() - (lastWholeRead.get(endpoint) ?? 0) >= RECONCILE_EVERY_MS;
      const whole = forceWhole.current || held.length === 0 || aged;
      const since = whole ? "" : watermark(held);
      if (whole) lastWholeRead.set(endpoint, Date.now());
      forceWhole.current = false;
      const answer = await searchHistoryStore!.load(since || undefined, signal);
      return since ? folded(held, answer) : answer;
    },
    enabled: Boolean(searchHistoryStore) && searchHistory,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const history: HistoryEntry[] = searchHistoryStore
    ? (storedHistory.data ?? [])
    : localHistory.map((term) => ({ term }));
  const historyChanged = () => queryClient.invalidateQueries({ queryKey: historyKeyQ });
  /** The newest `updated_at` held, which is what a delta is asked for. */
  const watermark = (held: HistoryEntry[]): string =>
    held.reduce(
      (newest, entry) =>
        entry.updated_at && entry.updated_at > newest ? entry.updated_at : newest,
      "",
    );
  /** Fold a "what changed" answer into a copy: newest wins, by id. */
  const folded = (held: HistoryEntry[], delta: HistoryEntry[]): HistoryEntry[] => {
    const byId = new Map(held.map((entry) => [entry.id ?? entry.term, entry]));
    for (const entry of delta) byId.set(entry.id ?? entry.term, entry);
    return [...byId.values()].sort((a, b) =>
      (b.updated_at ?? "").localeCompare(a.updated_at ?? ""),
    );
  };
  const mergeHistory = (delta: HistoryEntry[]) => {
    if (delta.length === 0) return;
    queryClient.setQueryData<HistoryEntry[]>(historyKeyQ, (held = []) => folded(held, delta));
  };
  // What the box actually offers: the copy we hold, narrowed by the Time Range
  // and by what is being typed. No request — that is the whole point of holding
  // it. Matching is on the stored form, which is stripped and lower-cased.
  const suggestions = (() => {
    const from = windowStart(historyWindow);
    const typed = typing.toLowerCase();
    return history.filter(
      (entry) =>
        (!typed || entry.term.startsWith(typed)) &&
        (!from || !entry.updated_at || entry.updated_at >= from),
    );
  })();
  // How many suggestions the dropdown is showing. The whole history is already
  // in the browser — a thousand terms, fetched once — so "load more" is a
  // number going up rather than a request going out. Scrolling the dropdown
  // raises it; nothing is fetched, ever.
  const suggestionBox = useRef<HTMLDivElement>(null);
  const [suggestionsShown, setSuggestionsShown] = useState(SUGGESTIONS_SHOWN);
  // Reset when the list being scrolled changes, adjusted during render rather
  // than in an effect: an effect would paint the old count once and correct it.
  const suggestionScope = `${typing}|${historyWindow}`;
  const [scrolledScope, setScrolledScope] = useState(suggestionScope);
  if (suggestionScope !== scrolledScope) {
    setScrolledScope(suggestionScope);
    setSuggestionsShown(SUGGESTIONS_SHOWN);
  }
  useLoadMore(suggestionBox, suggestionsShown < suggestions.length, false, () =>
    setSuggestionsShown((shown) => shown + SUGGESTIONS_SHOWN),
  );

  const [localHidden, setLocalHidden] = useState<string[]>(
    // A set, because the two audit columns are appended whether or not a
    // screen already named them, and a key listed twice is a column counted
    // twice by anything that measures this list.
    () =>
      readJson<string[]>(columnsKey) ?? [
        ...new Set([...hiddenByDefault, "updated_at", "updated_by_id"]),
      ],
  );
  /**
   * The order somebody dragged the columns into, by key. Empty until they do,
   * and then only a preference: a key it does not name — a column added to the
   * screen since — keeps its declared place at the end rather than disappearing.
   */
  const [localOrder, setLocalOrder] = useState<string[]>(
    () => readJson<string[]>(columnOrderKey) ?? [],
  );
  // The chosen columns are the browser's, and nothing else's. The list asks
  // for every column whatever is on screen, so showing one is a re-render and
  // not a request — which is what it looked like when the choice was a
  // projection the server had to answer first, and the two slowest requests on
  // the screen ran one after the other.
  /**
   * The same choice, kept with the account.
   *
   * The browser's copy above draws the list at once; this is what makes the
   * choice survive another device, a private window or cleared site data —
   * which it did not, and "I switched a column on and now it is off again"
   * was the browser forgetting. Adopted once when it arrives, and only when
   * the account has one saved: someone who has never chosen keeps whatever
   * this browser held, and the next Apply saves it to the account.
   */
  const listPrefsPath = `/me/list-preferences/${endpoint.replace(/^\/+/, "")}`;
  const savedPrefs = useQuery({
    queryKey: [listPrefsPath],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ hidden: string[]; order: string[]; saved: boolean }>(listPrefsPath, {
          signal,
        })
      ).data,
    staleTime: Infinity,
    retry: false,
  });
  const adoptedPrefs = useRef(false);
  useEffect(() => {
    const saved = savedPrefs.data;
    if (adoptedPrefs.current || !saved?.saved) return;
    adoptedPrefs.current = true;
    setLocalHidden(saved.hidden);
    writeJson(columnsKey, saved.hidden);
    setLocalOrder(saved.order);
    writeJson(columnOrderKey, saved.order);
  }, [savedPrefs.data, columnsKey, columnOrderKey]);
  const hidden = localHidden;
  const shown = (key: string) => !hidden.includes(key);
  const ordered = orderColumns(columns, localOrder);
  const visibleColumns = ordered.filter((c) => shown(c.key) && !c.inline);
  // What the *export* asks for, and nothing else. The list itself asks for
  // every column whatever is on screen — that is what makes showing one a
  // re-render rather than a request — but a download is a separate request
  // made once, deliberately, and it holds the columns the screen holds. Left
  // unprojected the file came back with all sixty-two, `Active Club Id` beside
  // `Active Club` and `Created By Id` beside `Created By`.
  // An inline column's fields are asked for too, since the cell it is drawn
  // inside needs them.
  // What the export dialog offers: every column, carrying the fields behind it,
  // so dropping one from the file drops its fields from the request rather than
  // trimming the answer afterwards. A–Z by label and not in table order, for
  // the reason `ColumnPicker` is: this list is read to *find* a column, and
  // table order is no help with that once there are thirty of them.
  const exportColumns = [...columns]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((c) => ({
      key: c.key,
      label: c.label,
      fields: c.fields ?? [c.key],
      selected: shown(c.key),
    }));
  /**
   * What a screen that owns its export downloads: the columns on screen, in
   * the order they are on screen.
   *
   * `exportColumns` above is a menu — every column, A–Z, so one can be found
   * and ticked — and it stays that shape for the panel that offers it. This is
   * the answer rather than the question: Customize Columns has already been
   * asked, and the file is what it said. Inline columns are in, because that
   * panel switches them and they carry a value of their own; `ordered` is the
   * table's own order, so the file reads left to right as the table does.
   */
  const chosenColumns = ordered
    .filter((c) => shown(c.key))
    .map((c) => ({ key: c.key, label: c.label, fields: c.fields ?? [c.key] }));
  const remember = (term: string) => {
    const value = term.trim();
    // Too short to have been searched, so too short to be worth keeping.
    if (!value || !longEnough(value)) return;
    if (searchHistoryStore) {
      const since = watermark(history);
      void searchHistoryStore.remember?.(value, since || undefined).then(mergeHistory);
      return;
    }
    const next = [value, ...localHistory.filter((h) => h !== value)].slice(0, 10);
    setLocalHistory(next);
    writeJson(historyKey, next);
  };
  /** Run the search that is in the box, and file it. What Enter does. */
  const commit = (term: string) => {
    const value = term.trim();
    setDeferredSearch(longEnough(value) ? value : "");
    setPage(1);
    remember(value);
  };
  const forget = (entry: { id?: number; term: string }) => {
    if (searchHistoryStore && entry.id !== undefined) {
      // The refetch this triggers has to be the whole list. A delta carries
      // what changed, and a row that is gone changed by not being there — ask
      // for what is new and the term just deleted comes straight back.
      forceWhole.current = true;
      void searchHistoryStore.forget(entry.id).then(historyChanged);
      return;
    }
    const next = localHistory.filter((h) => h !== entry.term);
    setLocalHistory(next);
    writeJson(historyKey, next);
  };

  // A scrolling list has no page and no sort: it is newest first, on and on.
  const params: ListParams = {
    search: deferredSearch,
    sort: infinite ? undefined : sort,
    order: infinite ? undefined : order,
    page: infinite ? undefined : page,
    size,
    ...filterValues,
  };
  const query = useQuery({
    queryKey: [endpoint, params],
    // The signal is what makes typing cheap: react-query aborts the request for
    // a term nobody is waiting for any more, so a search two keystrokes stale
    // stops costing the server anything the moment it is superseded.
    queryFn: ({ signal }) =>
      listMethod === "post"
        ? queryResource<T>(endpoint, params, signal)
        : listResource<T>(endpoint, params, signal),
    placeholderData: keepPreviousData,
    enabled: !infinite,
  });
  const feed = useInfiniteQuery({
    queryKey: [endpoint, params, "feed"],
    queryFn: ({ pageParam, signal }) =>
      listCursor<T>(endpoint, { ...params, after: pageParam }, signal),
    initialPageParam: undefined as string | number | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: keepPreviousData,
    enabled: infinite,
  });
  // Whichever is live answers for loading, error and refresh.
  const listing = infinite ? feed : query;
  const scroller = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLElement>(null);
  /**
   * How short the card may be made before the page scrolls instead of the
   * table being squeezed to a sliver behind its own pager.
   */
  const cardFloor = useTableFloor(card, scroller);
  // `isFetching`, not `isFetchingNextPage`: a refresh refetches every page
  // already loaded, so scrolling to the end while one is in flight would ask
  // for a page that answer is about to bring anyway.
  useLoadMore(
    scroller,
    infinite && !!feed.hasNextPage,
    feed.isFetching,
    () => void feed.fetchNextPage(),
  );
  /** The record on its own, straight from the API — for opening and reopening one. */
  const fetchOne = async (id: number): Promise<T> =>
    (await api.get<T>(`${endpoint}/${id}`)).data;
  /**
   * The list, and the dropdowns that are fed from it.
   *
   * A row added or changed here is a row every `/compact` picker reading this
   * endpoint would offer — so a supervisor created on this screen has to turn
   * up in the supervisor picker, and did not: the pickers cache under a key of
   * their own and were left holding the list as it was a moment ago.
   */
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [endpoint] });
    await queryClient.invalidateQueries({ queryKey: ["compact", `${endpoint}/compact`] });
  };

  // Two lists, because they cost different things.
  //
  // A `select` is a small static set — or one loader call — and a column can be
  // rendering it right now: the activity log prints a reason and a status
  // through this, and holding it back showed people `account_locked`. So it
  // always loads. A `fk` is a round trip per filter to a /compact endpoint, and
  // only matters once a chip or an open record actually shows an id, so it
  // waits for one.
  const selectFilters = filters.filter(
    (filter) => filter.type === "select" && !filter.dependsOn,
  );
  const fkFilters = filters.filter(
    (filter) => filter.type === "fk" && filter.endpoint && !filter.dependsOn,
  );
  const selectLookups = useQuery({
    queryKey: ["lookups", "select", endpoint, selectFilters.map((filter) => filter.name)],
    queryFn: async ({ signal }) =>
      Object.fromEntries(
        await Promise.all(
          selectFilters.map(async (filter) => [
            filter.name,
            new Map(
              (
                await (filter.loadOptions?.("", signal) ??
                  Promise.resolve(filter.options ?? []))
              ).map((option) => [String(option.value), option.label]),
            ),
          ]),
        ),
      ) as Record<string, Map<string, string>>,
    enabled: selectFilters.length > 0,
    staleTime: 5 * 60_000,
  });
  // `keepPreviousData` keeps the last answer on screen while the next one
  // loads, which is what stops the table flashing empty on every page switch.
  // It keeps it when the next one *fails* too — and then the screen is the
  // previous page's rows under this page's number, beside a total nothing on
  // it adds up to, with the failure invisible below the fold. A table quietly
  // answering a question nobody asked is worse than one saying it could not
  // answer, so a failed fetch has no rows, no total and no pager: the error
  // fills the table and the chrome around it stays exactly as it is.
  const failed = listing.isError;
  const rows = failed
    ? []
    : infinite
      ? (feed.data?.pages.flatMap((p) => p.items) ?? [])
      : (query.data?.items ?? []);
  // For `rowOnHand` above, which opens a record from the list instead of
  // reading it. Written during render, read in an effect, so what it holds is
  // the page that is on screen.
  rowsRef.current = rows;
  // Name the wait after whatever the person just did. "Loading" on a screen
  // where somebody pressed Search says less than "Searching members".
  const waitingFor: LoadingAction = deferredSearch
    ? "searching"
    : Object.values(filterValues).some((value) => value !== "")
      ? "filtering"
      : "loading";
  // The skeleton stands in for content that is not there yet, so it goes up at
  // once — there is nothing for it to flash over. The note is the opposite: on
  // a fast answer it would be a flicker, so it waits until the wait is real.
  const showSkeleton = listing.isPending;
  const slow = useDelayed(listing.isFetching);
  /*
   * What the chrome is held by — and it is not every read.
   *
   * `keepPreviousData` means a re-read leaves the previous rows on screen:
   * applying a saved set, dropping a chip, turning a page, sorting. Greying
   * the toolbar, the chips, the pager and the row menus for the length of
   * that takes the whole screen away from somebody who is still looking at
   * it — on Members, where the count runs past ten thousand, for seconds at
   * a time. Nothing about it is unsafe either: the query aborts the request
   * nobody is waiting for any more, so a second pick simply wins.
   *
   * So only the read with nothing behind it holds anything. Then the table is
   * a skeleton, there are no rows to act on, and a control that starts a
   * second piece of work — choosing columns, exporting, adding — is pressing
   * against an empty screen. Everything else says it is working by saying so:
   * the table dims, the refresh glyph spins, `aria-busy` is on the parts that
   * are re-reading.
   */
  const busy = listing.isPending;

  // Names for the ids on screen, and nothing else.
  //
  // A /compact response carries the id and the name together, so a label we
  // have been told once is never asked for again — scrolling the Club dropdown
  // names every club a chip, a column or a detail row can go on to show. What
  // is left is fetched by id, in one request per endpoint. The old shape was a
  // blanket page per endpoint, which is both more traffic and wrong past the
  // 200-row cap: the author of row 300 was in nobody's first page and rendered
  // as "300".
  const endpoints = [...new Set([...fkFilters.map((filter) => filter.endpoint!), ACCOUNTS])];
  const stores = useQueries({
    queries: endpoints.map((each) => ({
      queryKey: labelsKey(each),
      // Never fetches; `fetchCompact` fills it, whoever calls it and wherever.
      enabled: false,
      initialData: (): Record<string, string> => ({}),
      staleTime: Infinity,
    })),
  });
  const seen = (each: string): Record<string, string> =>
    (stores[endpoints.indexOf(each)]?.data as Record<string, string> | undefined) ?? EMPTY;
  /** Which endpoint names this id column, if any. */
  const endpointFor = (name: string): string | undefined =>
    ACCOUNT_LOOKUPS.has(name)
      ? ACCOUNTS
      : fkFilters.find((filter) => filter.name === name)?.endpoint;

  const onScreen = [...rows, viewing];
  const wanted = endpoints.map((each) => {
    const names = [...ACCOUNT_LOOKUPS, ...fkFilters.map((filter) => filter.name)].filter(
      (name) => endpointFor(name) === each,
    );
    const ids = new Set<number>();
    for (const name of names) {
      const picked = filterValues[name];
      const chosen = Number(Array.isArray(picked) ? (picked[0] ?? "") : (picked ?? ""));
      if (chosen > 0) ids.add(chosen);
      for (const row of onScreen) {
        const value = row ? (row as Record<string, unknown>)[name] : null;
        if (typeof value === "number" && value > 0) ids.add(value);
      }
    }
    const known = seen(each);
    return [...ids]
      .filter((id) => !(String(id) in known))
      .sort((a, b) => a - b)
      .slice(0, COMPACT_MAX);
  });
  // Nothing reads the results: `fetchCompact` files each response under its
  // endpoint, and `stores` is what re-renders when one lands.
  useQueries({
    queries: endpoints.map((each, index) => ({
      queryKey: ["compact", each, "ids", wanted[index]],
      queryFn: ({ signal }) => fetchCompact(each, { id: wanted[index] }, signal),
      enabled: (wanted[index]?.length ?? 0) > 0,
      staleTime: 5 * 60_000,
    })),
  });

  const lookup: Lookup = (filter, value) => {
    if (value === null) return "—";
    const key = String(value);
    // A yes/no filter stores the string the query carries, and every dropdown
    // that sets one offers Yes and No. A chip reporting it as `true` is the
    // same filter answering in a different language from the control that set
    // it — so it answers here in the one the person read.
    if (filters.find((each) => each.name === filter)?.type === "boolean") {
      if (key === "true") return t("rp.yes");
      if (key === "false") return t("rp.no");
    }
    const from = endpointFor(filter);
    return (
      selectLookups.data?.[filter]?.get(key) ?? (from ? seen(from)[key] : undefined) ?? key
    );
  };

  const activeFilters = Object.values(filterValues).filter(isSet).length;
  const applyFilters = (next: FilterValues) => {
    setFilterValues(next);
    setPage(1);
    setShowFilters(false);
  };
  const clearFilters = () => {
    setFilterValues({});
    setPage(1);
  };

  /**
   * The frame this list is drawn in, which is not the window: the navigation
   * takes three hundred pixels of it and gives them back, with no resize of
   * anything a media query can see.
   */
  const frame = useRef<HTMLDivElement>(null);
  const tight = useNarrow(frame, TOOLBAR_STACK);
  const pagerTight = useNarrow(frame, PAGER_TIGHT);
  const pagerBare = useNarrow(frame, PAGER_BARE);

  /**
   * The small scale: icons rather than worded buttons, 32-tall controls.
   *
   * A screen asks for it, or the frame forces it. Export, Import, Refresh and
   * Customize Columns are four worded buttons, and on a phone they wrap into
   * a block of text taller than the table it sits over — the same row of
   * glyphs fits on one line with room left for Add. `tight` is the frame, not
   * the window, so a wide page keeps its words even with the sidebar open.
   *
   * Not the `stacked` toolbar, which is legacy's Members row and is drawn the
   * way legacy draws it, words and all.
   */
  const compact = toolbarVariant === "compact" || (tight && toolbarLayout !== "stacked");
  /*
   * The toolbar's own scale.
   *
   * The compact row is the small one by design — a search, a funnel, three or
   * four icons and Add, all on one line — so its controls take the small size
   * rather than the form's default. The roomy toolbar keeps the full size,
   * where the row holds two or three worded buttons and has the width for them.
   */
  const buttonSize = compact ? "sm" : "md";
  const iconSize = compact ? "icon-sm" : "icon";
  // Stacked when the screen asks for it, and when the row has run out of
  // width whoever asked for what.
  /**
   * Three rows rather than two, and only where there is no room for one.
   *
   * `stacked` is legacy's Members toolbar: the search with the reload at the
   * end of it, the funnel and everything beside it on the next line, and Add
   * alone on the last, against the right edge. It is a shape for a narrow
   * frame — on a wide one the same controls fit on a single row with width to
   * spare, and three lines of toolbar over a table is three lines of nothing —
   * so it asks `tight` the same way `split` does, and a screen that sets it
   * keeps the ordinary inline row until the frame runs out.
   */
  const stacked = toolbarLayout === "stacked" && tight;
  const split = !stacked && (toolbarLayout === "split" || tight);
  /*
   * Where the left-hand group ends.
   *
   * The auto margin is what splits the row in two, so it has to sit on the
   * last thing on the left. That is the funnel wherever there is one: asking a
   * narrower question is what the search box does, and the two controls that do
   * it read as one pair when they stand together — which is how the compact row
   * and the split row have always drawn them, and how legacy draws the Members
   * toolbar. The roomy single row was the one place the funnel drifted off to
   * the far side to sit among Export and the column picker, which are jobs done
   * to the list rather than questions asked of it.
   *
   * A list with no filters has nothing to pair, so there the search is the whole
   * of the left group and keeps the margin itself.
   */
  const hasFilters = filters.length > 0 || !!renderFilters;
  const funnelEndsTheLeft = hasFilters;
  /*
   * The archive is one filter, set and cleared.
   *
   * Reusing the filter machinery rather than adding a second kind of state
   * means the chip row says which list you are on, the count is the archived
   * count, and clearing it from the filter panel does the same thing the icon
   * does. Paging resets, because page 4 of the live list is not page 4 of the
   * archive.
   */
  const archived = archiveFilter
    ? filterValues[archiveFilter.name] === archiveFilter.value
    : false;
  const toggleArchive = () => {
    if (!archiveFilter) return;
    setPage(1);
    setFilterValues((current) => {
      const { [archiveFilter.name]: _cleared, ...rest } = current;
      return archived ? rest : { ...rest, [archiveFilter.name]: archiveFilter.value };
    });
  };
  const sortBy = (key: string) => {
    setPage(1);
    if (key === sort) setOrder((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSort(key);
      setOrder("asc");
    }
  };

  /** The stamp of the row about to be deleted, from the list or the open detail. */
  const stampOf = (id: number) => {
    const row = rows.find((r) => r.id === id) ?? (viewing?.id === id ? viewing : undefined);
    return (row as { updated_at?: string } | undefined)?.updated_at;
  };
  const remove = useMutation({
    mutationFn: (id: number) => deleteResource(endpoint, id, stampOf(id)),
    onSuccess: () => {
      toast.success(t("rp.deleted").replace("{singular}", singular));
      void invalidate();
    },
    onError: (error) =>
      toast.error(
        errorMessage(error, t("rp.deleteFailed").replace("{singular}", singular.toLowerCase())),
      ),
  });

  /**
   * Back out of the archive.
   *
   * The stamp goes with it, as it does on every other write: restoring a row
   * somebody else has changed since you listed it is the same mistake as
   * saving over them.
   */
  /** Which row the restore question is about, if any. */
  const [restoring, setRestoring] = useState<T | null>(null);

  const bringBack = useMutation({
    mutationFn: (row: T) =>
      api.post(
        `${endpoint}/${row.id}/restore`,
        undefined,
        ifMatch(row as { updated_at?: string }),
      ),
    onSuccess: () => {
      toast.success(t("rp.restored").replace("{singular}", singular));
      void invalidate();
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          t("rp.restoreFailed").replace("{singular}", singular.toLowerCase()),
        ),
      ),
  });

  /**
   * The ticked rows, all at once: archived from the list, restored from the
   * archive. The server tries each on its own and reports the ones it would
   * not move, so this ends in a count and, when there is one, a list to read.
   */
  const mayBulk = Boolean(bulk) && (!bulk?.permission || can(profile, bulk.permission));
  const bulkPath = archived
    ? (bulk?.restorePath ?? "bulk-restore")
    : (bulk?.deletePath ?? "bulk-delete");

  const actOnTicked = useMutation({
    mutationFn: async (ids: number[]) => {
      const { data } = await api.post<BulkAnswer>(`${endpoint}/${bulkPath}`, { ids });
      return data;
    },
    onSuccess: (answer) => {
      const noun = (answer.done === 1 ? singular : plural).toLowerCase();
      if (answer.done > 0) {
        toast.success(
          t(archived ? "rp.bulkRestored" : "rp.bulkDeleted")
            .replace("{count}", String(answer.done))
            .replace("{plural}", noun),
        );
      }
      // The refused rows are not a toast: they are a list with a reason each,
      // and a toast is gone before the third one has been read.
      setRefused(answer.refused.length > 0 ? answer.refused : null);
      setTicked(new Set());
      void invalidate();
    },
    onError: (error) =>
      toast.error(
        errorMessage(error, t(archived ? "rp.restoreManyFailed" : "rp.deleteManyFailed")),
      ),
  });

  /**
   * A row's menu: Restore at the top while the archive is on screen, then
   * whatever the screen adds of its own.
   */
  const menuFor = (row: T, refresh: () => void): RowAction[] => {
    const own = rowActions?.(row, refresh) ?? [];
    const mayRestore =
      archived && restore && (!restore.permission || can(profile, restore.permission));
    return mayRestore
      ? [
          {
            label: t("rp.restore"),
            icon: ArchiveRestore,
            run: () => setRestoring(row),
          },
          ...own,
        ]
      : own;
  };

  /**
   * A new question empties the selection.
   *
   * Ticks are made against rows on screen, and a filter, a search or a page
   * turn replaces those rows. Carrying the old ids forward would mean a Delete
   * button counting rows nobody can see, acting on them out of sight.
   */
  const question = JSON.stringify(params);
  useEffect(() => {
    setTicked((current) => (current.size === 0 ? current : new Set()));
  }, [question]);

  /** Whether every row on this page is ticked, and whether any of them is. */
  const pageTicked = rows.length > 0 && rows.every((row) => ticked.has(row.id));
  const somePageTicked = rows.some((row) => ticked.has(row.id));

  const tickOne = (id: number) =>
    setTicked((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  /** The page's box: adds this page to the selection, or takes it back out. */
  const tickPage = (on: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      for (const row of rows)
        if (on) next.add(row.id);
        else next.delete(row.id);
      return next;
    });

  /** A row named for the box's label, so the boxes are not forty "Select row". */
  const rowLabel = (row: T): string => {
    const named = row as { name?: string; code?: string };
    return named.name ?? named.code ?? String(row.id);
  };

  const actions = !noRowMenu && !(readOnly && noView);
  // The same two things that decide whether the view column is a button: with
  // no detail view there is nothing to open, and a deleted row is read from
  // the archive rather than opened out of it.
  const openRowOnDouble = openOnRowDoubleClick && !noView && !archived;
  const total = failed ? 0 : (query.data?.total ?? 0);
  // The server stops counting at `countCap` and reports a floor, because an
  // exact total over tens of millions means reading every matching row — 20s
  // on the filters people actually use. So the number is shown as "10,000+"
  // once it reaches the cap: the same answer, and the same next move, without
  // the wait. A list with no cap set is counted exactly and reads exactly.
  //
  // `total - 1` because a capped total is a floor built as "what has been read,
  // and one more": the row past the cap is what proves there is more, and is
  // not itself a count. So ten thousand and one reads "10,000+", and page three
  // hundred of the same list reads "15,050+" rather than falling back to the cap.
  const capped = countCap !== undefined && total > countCap;
  const totalLabel = capped ? `${(total - 1).toLocaleString()}+` : total.toLocaleString();
  // The first and last row of this page — "26-50 of 75". Counted from
  // `rows.length` rather than `size`, so the last page reads to where the
  // list actually stops instead of promising a full page of rows.
  const firstRow = (page - 1) * size + 1;
  const lastRow = (page - 1) * size + rows.length;
  const pages = failed ? 1 : Math.max(1, query.data?.pages ?? 1);
  // Paging is `LIMIT … OFFSET`, and an offset is rows the database walks and
  // throws away before it returns anything. On a list of forty million that
  // makes "last page" `OFFSET 79,999,950` — a table scan for fifty rows, and
  // on a partitioned table, one per partition.
  //
  // So past this the end of the list is not offered at all: neither the `»`
  // button nor the `… 1,600,000` beside it. Not disabled — gone. A control
  // that is greyed out for a reason nothing on screen explains reads as
  // broken, and the row count above the pager already says how long the list
  // is, which is what the page number was standing in for. Short lists keep
  // both, which is where the jump was ever any use: on thirty clubs the last
  // page is the second.
  const canJumpToEnd = (pages - 1) * size <= DEEP_PAGE_ROWS;

  const confirmDelete = (id: number) => setDeleting(id);

  /**
   * Asked wherever Delete was pressed.
   *
   * The detail view used to close itself and hand the question to the list,
   * so answering "no" left you somewhere you had not asked to be. It stays put
   * now; only an actual delete closes it, because the record is gone.
   */
  /** The row waiting on a yes before it comes back out of the archive. */
  const restoreDialog = restore ? (
    <ConfirmDialog
      open={restoring !== null}
      onClose={() => setRestoring(null)}
      onConfirm={() => {
        if (restoring === null) return;
        bringBack.mutate(restoring, { onSettled: () => setRestoring(null) });
      }}
      loading={bringBack.isPending}
      tone="warning"
      title={t("rp.confirmRestoreTitle").replace("{singular}", singular)}
      message={t("rp.confirmRestore").replace("{singular}", singular.toLowerCase())}
      confirmLabel={t("rp.restore")}
      cancelLabel={t("common.cancel")}
    />
  ) : null;

  const bulkDialog = mayBulk ? (
    <ConfirmDialog
      open={confirmingBulk}
      onClose={() => setConfirmingBulk(false)}
      onConfirm={() =>
        actOnTicked.mutate([...ticked], { onSettled: () => setConfirmingBulk(false) })
      }
      loading={actOnTicked.isPending}
      tone={archived ? "warning" : "danger"}
      title={t(archived ? "rp.confirmRestoreManyTitle" : "rp.confirmDeleteManyTitle").replace(
        "{count}",
        String(ticked.size),
      )}
      message={t(archived ? "rp.confirmRestoreMany" : "rp.confirmDeleteMany").replace(
        "{plural}",
        (ticked.size === 1 ? singular : plural).toLowerCase(),
      )}
      confirmLabel={t(archived ? "rp.restore" : "common.delete")}
      cancelLabel={t("common.cancel")}
    />
  ) : null;

  /**
   * What would not move, after the rest has. A list rather than a message:
   * each row has its own reason, and the reason is the whole point — a
   * province with cities under it is a different problem from one that was
   * deleted while the page was open.
   */
  const refusedDialog = refused ? (
    <Modal
      open
      onClose={() => setRefused(null)}
      size="md"
      title={t("rp.refusedTitle").replace("{count}", String(refused.length))}
      description={t(archived ? "rp.refusedRestoreBody" : "rp.refusedDeleteBody")}
      footer={
        <Button variant="secondary" onClick={() => setRefused(null)}>
          {t("common.close")}
        </Button>
      }
    >
      <ul className="border-line max-h-72 overflow-y-auto rounded-lg border">
        {refused.map((row) => (
          <li
            key={row.id}
            className="border-line text-callout flex gap-3 border-b px-3 py-2 last:border-b-0"
          >
            <span className="font-medium">{row.name ?? `#${row.id}`}</span>
            <span className="text-fg-muted min-w-0">{row.message}</span>
          </li>
        ))}
      </ul>
    </Modal>
  ) : null;

  const confirmDialog = (
    <ConfirmDialog
      open={deleting !== null}
      onClose={() => setDeleting(null)}
      onConfirm={() => {
        if (deleting === null) return;
        remove.mutate(deleting, {
          onSuccess: () => open(null),
          onSettled: () => setDeleting(null),
        });
      }}
      loading={remove.isPending}
      title={t("rp.confirmDeleteTitle").replace("{singular}", singular)}
      message={
        confirmDeleteMessage ??
        t("rp.confirmDelete").replace("{singular}", singular.toLowerCase())
      }
      confirmLabel={t("common.delete")}
    />
  );

  /** Every filter that is currently narrowing the list, named and removable. */
  const chips = Object.entries(filterValues)
    .filter(([, value]) => isSet(value))
    .map(([name, value]) => ({
      name,
      label: filters.find((filter) => filter.name === name)?.label ?? humanize(name),
      // Several values read as one chip: "Gold, Business", which is what the
      // filter says, rather than a chip each for a single condition.
      value: (Array.isArray(value) ? value : [value])
        .map((one) => lookup(name, one))
        .join(", "),
    }));

  /**
   * A record is read when it is opened, so between the click and the answer
   * there is nothing to draw the panel from.
   *
   * Nothing opens until the record is in hand. The wait is a spinner over the
   * screen that was already there, and it takes the pointer while it is up, so
   * a second row cannot be clicked into the same wait. Drawing a skeleton
   * instead meant a sheet that opened empty and filled a moment later, and a
   * sheet whose shape or title changed under the reader — which is two things
   * happening for one click.
   */
  const openingEdit = opening && editToken !== undefined;
  const openingView = opening && !openingEdit;
  /**
   * The wait itself: fixed over everything, and `pointer-events` on the sheet
   * rather than `none`, so it swallows clicks instead of letting them through
   * to the list underneath.
   */
  const openingBlocker = opening ? (
    <div
      role="status"
      aria-live="polite"
      aria-label={t("common.loading")}
      className="bg-scrim/40 fixed inset-0 z-50 grid place-items-center backdrop-blur-[1px]"
    >
      <span className="bg-surface-raised text-fg-muted text-callout flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[var(--shadow-float)]">
        <Spinner />
        {t("common.loading")}
      </span>
    </div>
  ) : null;

  // A bespoke form takes the page over exactly as the generic one does — it is
  // still a create or an edit, and it clears the `edit` parameter on the way
  // out the same way. Rendered after the list instead, it opened underneath a
  // table that was still on screen.
  // Opened from a record, `view` is still in the address bar: clearing `edit`
  // alone lands back on that record, and the row is refetched so it shows what
  // was just saved. Declared out here because a modal form is rendered from the
  // list's own return, below, rather than in place of it.
  const closeForm = () => {
    setEditing(null);
    setParam("edit", null);
  };
  const formSaved = () => {
    // Read before `closeForm` clears it: whether what was just saved is the
    // person doing the saving.
    const savedSelf = editing !== null && editing !== "new" && profile?.id === editing.id;
    closeForm();
    if (editing && editing !== "new" && viewing) void fetchOne(editing.id).then(setViewing);
    void invalidate();
    // Their own account is also the profile every screen reads them from — the
    // club a new record is stamped with, the permissions the menus follow. It
    // is cached for a minute, so without this the form they open next is filled
    // in from the account as it was before they changed it.
    if (savedSelf) void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
  };

  /**
   * The generic field form, in whichever frame the screen asked for.
   *
   * One definition for both: the panel and the modal differ only in what is
   * drawn around the fields, and keeping two copies of the initial-values and
   * concurrency-stamp wiring is how the two quietly stop matching.
   */
  const genericForm = (variant: "panel" | "modal") =>
    editing ? (
      <EditDialog
        variant={variant}
        singular={singular}
        endpoint={endpoint}
        fields={fields}
        columns={formColumns}
        initial={
          editing === "new"
            ? newDefaults && profile
              ? newDefaults(profile)
              : {}
            : toForm(editing)
        }
        id={editing === "new" ? null : editing.id}
        stamp={editing === "new" ? null : (editing as { updated_at?: string }).updated_at}
        onClose={closeForm}
        onSaved={formSaved}
      />
    ) : null;

  // A modal form is an overlay on whatever is behind it — the list, or the
  // record it was opened from — so it is rendered alongside each of those
  // rather than in place of them. A screen with no form of its own gets the
  // generic one in the same frame.
  const formOverlay =
    editing && formVariant === "modal"
      ? renderForm
        ? renderForm(editing, closeForm, formSaved)
        : genericForm("modal")
      : null;

  if (editing && !formOverlay) {
    if (renderForm) return <Page>{renderForm(editing, closeForm, formSaved)}</Page>;
    return <Page>{genericForm("panel")}</Page>;
  }

  // Adding and editing take over the page, the way the configuration screens
  // and the role editor do: a form that replaces what it is a form for, rather
  // than a sheet over a table nobody can use while it is open.
  // Viewing takes over the page too, so a record is read where it was opened
  // rather than in a drawer over the table it came from — and can be acted on
  // there. Edit and Delete follow the same flags the row menu does.
  // The same idea as `formOverlay`: a modal detail view is a sheet over the
  // list, so it must not take the page over — it is rendered alongside it.
  const viewCtx = (row: T): DetailContext => ({
    onClose: () => open(null),
    lookup,
    busy: refreshingOne,
    onRefresh: () => {
      setRefreshingOne(true);
      void fetchOne(row.id)
        .then(setViewing)
        .finally(() => setRefreshingOne(false));
    },
    onEdit:
      readOnly || noEdit || rowCanEdit?.(row, profile) === false
        ? undefined
        : () => setParam("edit", row.id),
    onDelete:
      readOnly || noDelete || rowCanDelete?.(row, profile) === false
        ? undefined
        : () => confirmDelete(row.id),
  });
  /** The generic detail body, in whichever frame the screen asked for. */
  const genericView = (variant: "panel" | "modal", row: T, ctx: DetailContext) => (
    <DetailPanel
      variant={variant}
      singular={singular}
      screenTitle={title}
      row={row}
      {...ctx}
      extra={renderDetailExtra?.(row)}
      hide={detailHide}
      sections={detailSections}
      labels={detailLabels}
      actions={menuFor(row, () => {
        void fetchOne(row.id).then(setViewing);
        void invalidate();
      })}
    />
  );

  const viewOverlay =
    viewing && viewVariant === "modal"
      ? renderView
        ? renderView(viewing, viewCtx(viewing))
        : genericView("modal", viewing, viewCtx(viewing))
      : null;

  if (viewing && !viewOverlay) {
    const ctx: DetailContext = {
      onClose: () => open(null),
      lookup,
      busy: refreshingOne,
      onRefresh: () => {
        setRefreshingOne(true);
        void fetchOne(viewing.id)
          .then(setViewing)
          .finally(() => setRefreshingOne(false));
      },
      onEdit:
        readOnly || noEdit || rowCanEdit?.(viewing, profile) === false
          ? undefined
          : () => setParam("edit", viewing.id),
      onDelete:
        readOnly || noDelete || rowCanDelete?.(viewing, profile) === false
          ? undefined
          : () => confirmDelete(viewing.id),
    };
    if (renderView) {
      return (
        <Page>
          {renderView(viewing, ctx)}
          {formOverlay}
          {confirmDialog}
        </Page>
      );
    }
    return (
      <Page>
        {/* The edit leaves `view` in the address bar, so closing or saving it
            comes back to this record rather than dropping to the list. */}
        {genericView("panel", viewing, ctx)}
        {formOverlay}
        {confirmDialog}
        {restoreDialog}
        {bulkDialog}
        {refusedDialog}
      </Page>
    );
  }

  /**
   * A bare icon on a compact row, or wherever a screen asks for one.
   *
   * Compact is a row of glyphs — the funnel, the columns, the archive — and a
   * lone worded button among them is the odd one out. Everywhere else it keeps
   * its word, beside the other worded buttons.
   */
  const refreshControl =
    iconRefresh || compact ? (
      <IconButton
        variant="ghost"
        size={iconSize}
        label={t("rp.refresh")}
        disabled={busy}
        aria-busy={listing.isFetching || undefined}
        onClick={() => void listing.refetch()}
      >
        <RotateCw className={cn(listing.isFetching && "animate-spin")} aria-hidden />
      </IconButton>
    ) : (
      <Button
        variant="secondary"
        disabled={busy}
        aria-busy={listing.isFetching || undefined}
        onClick={() => void listing.refetch()}
      >
        <RotateCw className={cn(listing.isFetching && "animate-spin")} aria-hidden />
        {t("rp.refresh")}
      </Button>
    );

  /**
   * Icon on a compact row, a worded button everywhere else.
   *
   * Compact used to drop the picker altogether. But which columns a list shows
   * is not a fifth worded button's worth of noise — it is one glyph, and the
   * screens that hide columns by default are exactly the ones that need a way
   * back to them. A screen that truly has no choice to offer sets
   * `columnPicker: false` and says so.
   */
  const columnsControl = !columnPicker ? null : compact ? (
    <IconButton
      variant="ghost"
      size={iconSize}
      label={t("rp.columns")}
      disabled={busy}
      onClick={() => setPickingColumns(true)}
    >
      <Columns3Cog aria-hidden />
    </IconButton>
  ) : (
    <Button
      variant="secondary"
      size={buttonSize}
      disabled={busy}
      onClick={() => setPickingColumns(true)}
    >
      <Columns3 aria-hidden />
      {t("rp.customizeColumns")}
    </Button>
  );

  /*
   * Hidden outright for anybody who may not use it, rather than shown and
   * refused: an icon that never does anything is furniture. The label says
   * which way it goes, so the same control reads correctly from both lists.
   */
  const archiveControl =
    !archiveFilter ||
    (archiveFilter.permission && !can(profile, archiveFilter.permission)) ? null : (
      <IconButton
        variant={archived ? "secondary" : "ghost"}
        size={iconSize}
        label={archived ? t("rp.exitArchive") : t("rp.archive")}
        aria-pressed={archived}
        disabled={busy}
        className={cn(archived && "text-accent")}
        onClick={toggleArchive}
      >
        {archived ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />}
      </IconButton>
    );

  /**
   * The search box and the history panel under it: the left edge of the row,
   * whichever way the toolbar is laid out.
   */
  const searchControl = (
    <div
      className={cn(
        "relative w-full",
        // Stacked, the box takes what is left of its line rather than the
        // whole of it, so the funnel stays beside it instead of wrapping onto
        // a line of its own. A phone is always stacked — the toolbar splits
        // under 720px — and a funnel alone on a row reads as a control that
        // belongs to nothing. `flex-1` rather than a narrower width: the line
        // is as wide as the screen and the search should have all of it that
        // the funnel does not want. From `sm` the fixed width is back and
        // this changes nothing.
        //
        // The floor is what keeps the pairing honest on a screen that puts
        // more than a funnel here — saved filters, say. Without it every
        // extra squeezes the box instead of wrapping, and a search input an
        // inch wide is worse than one on its own line. With it the extras go
        // under and the funnel, which is 32px, always still fits.
        (split || stacked) && "min-w-[9rem] flex-1 sm:flex-none",
        // The auto margin is what holds the left edge on a single row. Split,
        // the actions are on a line of their own and the filter controls sit
        // beside the box rather than an inch of nothing.
        !funnelEndsTheLeft && !split && "sm:mr-auto",
        compact ? "sm:w-60" : "sm:w-80",
      )}
    >
      <SearchInput
        // 32, on both variants: this box used to be the one 40 in a row of 32s
        // on the roomy toolbar, which made the Members search read as a
        // different control from the search on every other list.
        fieldSize="sm"
        value={search}
        onChange={(event) => {
          setPage(1);
          setSearch(event.target.value);
        }}
        onClear={() => {
          setPage(1);
          setSearch("");
          setDeferredSearch("");
        }}
        onFocus={() => setSearchFocus(true)}
        onBlur={() => setTimeout(() => setSearchFocus(false), 150)}
        // `readOnly` stops typing and nothing else: Enter still fires, and
        // would start a second read while the first is in the air.
        onKeyDown={(event) => event.key === "Enter" && !listing.isFetching && commit(search)}
        // Held only where a search is committed rather than typed. On a
        // `searchOnCommit` screen the box locks between Enter and the
        // answer, which is a real wait a person chose to start — any read,
        // not only the first, which is why this one asks `isFetching` rather
        // than `busy`. Where the list follows the typing, a lock on the read
        // would take the keyboard away mid-word, which is the members-search
        // bug this session opened with.
        readOnly={searchOnCommit && listing.isFetching}
        maxLength={searchMaxLength}
        aria-busy={listing.isFetching}
        placeholder={searchPlaceholder ?? t("rp.search")}
        aria-label={`${t("rp.search")} ${title.toLowerCase()}`}
      />
      {searchHistory && searchFocus ? (
        <div
          className={cn(
            "border-line bg-surface-raised animate-sheet absolute top-full left-0 z-30 mt-1.5 grid max-w-[calc(100vw-2.5rem)] grid-cols-1 overflow-hidden rounded-xl border shadow-[var(--shadow-float)]",
            searchHistoryStore ? "w-[38rem] sm:grid-cols-[minmax(0,1fr)_13rem]" : "w-[24rem]",
          )}
        >
          <div className="p-2">
            <p className="text-fg-subtle text-footnote px-2 pt-1 pb-1.5 font-medium">
              {typing ? t("rp.suggestions") : t("rp.searchHistory")}
            </p>
            {/* Below the minimum the list is deliberately left alone. Said out
                loud, because silence here reads as a search that matched
                nothing — the box looks filled in and the table looks unfiltered,
                and nothing on screen connects the two. */}
            {search.trim() && !longEnough(search) ? (
              <p className="text-fg-muted text-footnote px-2 pb-1.5">
                {t("rp.searchMinLength").replace("{count}", String(searchMinLength))}
              </p>
            ) : null}
            {suggestions.length === 0 ? (
              <p className="text-fg-subtle text-subhead px-2 py-3">{t("common.nothingYet")}</p>
            ) : (
              <div ref={suggestionBox} className="max-h-80 overflow-y-auto">
                {suggestions.slice(0, suggestionsShown).map((entry) => (
                  <div
                    key={entry.id ?? entry.term}
                    className="hover:bg-surface-inset flex items-center gap-1 rounded-lg pr-1"
                  >
                    <button
                      type="button"
                      disabled={busy}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        // What the row shows is what the box gets; the match is case-blind.
                        setSearch(titleCase(entry.term));
                        commit(entry.term);
                      }}
                      className="text-fg text-callout flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left"
                    >
                      <Search className="text-fg-subtle size-4 shrink-0" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{titleCase(entry.term)}</span>
                      {entry.updated_at ? (
                        <span className="text-fg-subtle text-footnote shrink-0 tabular-nums">
                          {fmtDate(entry.updated_at)}
                        </span>
                      ) : null}
                    </button>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => forget(entry)}
                      className="text-fg-subtle hover:text-fg grid size-6 shrink-0 place-items-center rounded-full"
                    >
                      <X className="size-3.5" aria-hidden />
                      <span className="sr-only">
                        {t("rp.removeSearch").replace("{term}", entry.term)}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {searchHistoryStore ? (
              // Server-kept history is swept a month after a term was last
              // used, so say so rather than let people wonder where an old
              // search went. The browser-kept one has no such window.
              <p className="text-fg-subtle text-footnote border-line mt-1.5 border-t px-2 pt-2">
                {t("rp.searchHistoryKept")}
              </p>
            ) : null}
          </div>
          {searchHistoryStore ? (
            <div className="bg-surface-sunken border-line p-2 sm:border-l">
              <p className="text-fg-subtle text-footnote px-2 pt-1 pb-1.5 font-medium">
                {t("rp.timeRange")}
              </p>
              {(
                [
                  ["hour", t("rp.lastHour")],
                  ["day", t("rp.last24h")],
                  ["week", t("rp.last7d")],
                  ["month", t("rp.last4w")],
                  ["", t("rp.allTime")],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setHistoryWindow(value)}
                  className={cn(
                    "text-callout block w-full rounded-lg px-2.5 py-2 text-left transition-colors",
                    historyWindow === value
                      ? "text-accent bg-surface-raised font-medium"
                      : "text-fg hover:bg-surface-raised",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  /** The funnel, worded or not, carrying the count of what is narrowing the list. */
  const filterControl = !hasFilters ? null : compact ? (
    // Icon only, with the count as a badge on it — clearing lives in
    // the panel it opens, not as another button on this row.
    <IconButton
      variant="ghost"
      size={iconSize}
      label={filterLabel ?? t("rp.filters")}
      disabled={busy}
      aria-expanded={showFilters}
      className={cn("relative shrink-0 sm:mr-auto", activeFilters > 0 && "text-accent")}
      onClick={() => setShowFilters((open) => !open)}
    >
      <Funnel aria-hidden />
      {activeFilters > 0 ? (
        <span
          aria-hidden
          className={cn(
            "bg-accent text-accent-fg absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full px-1",
            "text-[0.625rem] leading-none font-medium tabular-nums",
            // The same pill the selection count wears, ringed in the page's
            // colour so it sits on the icon rather than in it.
            "ring-2 ring-[var(--canvas)]",
          )}
        >
          {activeFilters > 99 ? "99+" : activeFilters}
        </span>
      ) : null}
    </IconButton>
  ) : (
    <Button
      variant="secondary"
      disabled={busy}
      className={cn(activeFilters > 0 && "border-accent text-accent")}
      aria-expanded={showFilters}
      onClick={() => setShowFilters((open) => !open)}
    >
      <Funnel aria-hidden />
      {filterLabel ?? t("rp.filters")}
      {activeFilters > 0 ? (
        <span className="bg-accent text-accent-fg text-footnote grid min-w-5 place-items-center rounded-full px-1.5 tabular-nums">
          {activeFilters}
        </span>
      ) : null}
    </Button>
  );

  /* Saved filters and anything else a screen puts beside the filter button.
     Applying one is a read the previous rows stay up through, so the menu
     stays live through it: picking a second set, or picking the same one
     again to take it off, is allowed while the first is still landing. */
  const filterExtraControl = (
    <fieldset
      disabled={busy}
      className="contents [&:disabled_a]:pointer-events-none [&:disabled_a]:opacity-50"
    >
      {filterExtra?.(filterValues, applyFilters)}
    </fieldset>
  );

  /**
   * What Export does, named because two shapes of the same control share it:
   * an icon on the compact row, a worded button on the roomy one.
   */
  const startExport = () => {
    // A file the server writes is one the person chooses the columns for, so
    // the click opens the picker rather than writing anything.
    if (exportServer) {
      setExporting(true);
      return;
    }
    /**
     * A screen that owns its export owns all of it, however short the list is.
     *
     * This used to hand the job back whenever the page happened to hold every
     * match — twenty-five rows written straight out of the browser — and go to
     * the screen's own handler only past that. Two files from one button, and
     * which one you got depended on how narrow the filter was: the short one
     * came from the rows the list had already fetched, the long one from the
     * server, and they did not have to agree about anything. One path now.
     */
    if (exportAll) {
      exportAll({
        params,
        onScreen: rows.length,
        total,
        totalLabel,
        capped,
        columns: chosenColumns,
      });
      return;
    }
    exportCsv(csvBase, visibleColumns, rows);
  };

  /**
   * Everything that acts on the list, in the order the design reads it: the
   * screen's own headline action first, then the two controls every list has
   * — Export, Refresh and Customize Columns — then the screen's menus, and
   * Add last so it is the rightmost thing on whichever line it lands on.
   *
   * One fieldset over all of them, so a screen's slots follow `busy` without
   * each caller threading it into every button it supplies; `contents`, so
   * they keep their own places in the row. A fieldset disables form controls
   * and stops at anchors, and a screen's slots hold both — Members puts an
   * Activity Log link in one — so the rule reaches those too.
   */
  /**
   * The actions, as the groups the stacked toolbar lays out on three lines.
   *
   * Every layout composes the same pieces in the same order, so the inline and
   * split rows are exactly what they were — only the stacked one breaks them
   * apart. The grouping is legacy's, and it is a reasonable one: the reload
   * belongs at the end of the search line, because it is the search run again;
   * everything that reshapes the list stands with the funnel on the line under
   * it; and the one button that makes a record keeps the last line to itself.
   */
  const dataControls = (
    <>
      {menuExtra}
      {/* Import and Export are icons on the compact row and worded buttons on
          the roomy one, the way Refresh and Customize Columns already are: the
          compact row is four icons and Add, and two more worded buttons on it
          is the wrap it was drawn to avoid. The label is the tooltip and the
          accessible name either way. */}
      {/* The arrow reads against the system, not against the disk. Export
          sends rows out of it, so the arrow points out; Import brings them in,
          so it points in. Drawn the other way round — the browser's sense, in
          which exporting is a download — the pair said the opposite of the
          word beside it, and the two buttons sit side by side. */}
      {exportable ? (
        compact ? (
          <IconButton
            variant="ghost"
            size={iconSize}
            label={t("rp.export")}
            onClick={startExport}
          >
            <Upload aria-hidden />
          </IconButton>
        ) : (
          <Button variant="secondary" size={buttonSize} onClick={startExport}>
            <Upload aria-hidden />
            {t("rp.export")}
          </Button>
        )
      ) : null}
      {importable && (!importable.permission || can(profile, importable.permission)) ? (
        compact ? (
          <IconButton
            variant="ghost"
            size={iconSize}
            label={t("rp.import")}
            onClick={() => setImporting(true)}
          >
            <Download aria-hidden />
          </IconButton>
        ) : (
          <Button variant="secondary" size={buttonSize} onClick={() => setImporting(true)}>
            <Download aria-hidden />
            {t("rp.import")}
          </Button>
        )
      ) : null}
      {/* One button for the ticked rows, and only while there are some: a
          control that spends its life disabled is furniture, and this one is
          destructive enough that it should not be sitting there by default.
          Delete on the list, Restore in the archive — the same pair the row
          menu offers, applied to the selection. */}
      {mayBulk && ticked.size > 0 ? (
        // An icon like the rest of the row, with the count as a badge on it —
        // the funnel beside it carries its own count the same way. The word is
        // the tooltip and the accessible name, so "Delete 3" is still what a
        // screen reader announces and what the button is called.
        <IconButton
          variant="ghost"
          size={iconSize}
          label={t(archived ? "rp.restoreTicked" : "rp.deleteTicked").replace(
            "{count}",
            String(ticked.size),
          )}
          className={cn("relative shrink-0", !archived && "text-danger")}
          onClick={() => setConfirmingBulk(true)}
        >
          {archived ? <ArchiveRestore aria-hidden /> : <Trash2 aria-hidden />}
          {/* A pill rather than a circle: three digits in a 16px circle is a
              circle with three digits spilling out of it. It grows sideways
              and stops at 99+, and a ring in the page's own colour keeps it
              off the icon underneath instead of merging with it. */}
          <span
            aria-hidden
            className={cn(
              "absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full px-1",
              "text-[0.625rem] leading-none font-medium tabular-nums",
              "ring-2 ring-[var(--canvas)]",
              archived ? "bg-accent text-accent-fg" : "bg-[var(--color-danger-500)] text-white",
            )}
          >
            {ticked.size > 99 ? "99+" : ticked.size}
          </span>
        </IconButton>
      ) : null}
    </>
  );

  /** What reshapes the list rather than what is in it. */
  const viewControls = (
    <>
      {columnsControl}
      {archiveControl}
      {toolbar}
    </>
  );

  const addControl =
    readOnly || !canAdd
      ? null
      : (addMenu ?? (
          <Button size={buttonSize} onClick={() => setEditing("new")}>
            {addLabel ?? t("rp.add").replace("{singular}", singular)}
          </Button>
        ));

  /**
   * One `disabled` over a whole group. `contents`, so the fieldset lays nothing
   * out and the row it sits in still sees the buttons as its own children —
   * which is what lets a group be split across three rows and still go quiet
   * together while a read is in the air.
   */
  const asGroup = (children: ReactNode) => (
    <fieldset
      disabled={busy}
      className="contents [&:disabled_a]:pointer-events-none [&:disabled_a]:opacity-50"
    >
      {children}
    </fieldset>
  );

  // The order the row has always had: the reload sits between what acts on
  // the data and what reshapes the view of it.
  const actionControls = asGroup(
    <>
      {dataControls}
      {refreshControl}
      {viewControls}
      {addControl}
    </>,
  );

  return (
    <Page fill ref={frame}>
      <PageHeader
        title={title}
        description={description}
        back={
          // Only where the way back is a way in. A role can hold
          // `view__member_log` and not `view__member` — the trail of what
          // happened is not the records it happened to — and the arrow up to
          // Members was then a one-way trip to Unauthorized. Same for a parent
          // that is readable but hidden from this person's interface.
          backTo &&
          gateForPath(backTo)(
            (...codenames) => can(profile, ...codenames),
            (...codenames) => hiddenFrom(profile, ...codenames),
          ) ? (
            <Link to={backTo} className={backLinkClass}>
              <BackArrow label={t("rp.back")} />
            </Link>
          ) : undefined
        }
      />

      {/* Above the toolbar because it is above what the toolbar does: the cards
          say what is in the list before anything is asked of it, and pressing
          one is a filter, which is why it is handed the same pair `filterExtra`
          gets rather than being a static header. */}
      {banner?.(filterValues, applyFilters)}

      {/* One row or two. Inline, the search box holds the left edge with an auto
          margin of its own and every action packs against the right; what does not
          fit wraps, Add among it and last. Split, the search box and the filter
          controls keep a line to themselves and the actions have their own line
          under it, against the right edge — a toolbar wide enough to wrap by
          accident reads better broken on purpose. */}
      {stacked ? (
        // Legacy's three lines, in legacy's order. The reload ends the search
        // line because it is the search run again; the funnel and what stands
        // beside it get the line under it; Add keeps the last one, against the
        // right edge, where nothing else can be hit on the way to it.
        // `flex-nowrap` overrides the base `flex-wrap`, and it is load-bearing.
        // On a column flex container a *wrapping* line takes its cross size
        // from the widest item's max-content width — so the scrolling row
        // below stretched the toolbar to the full width of its buttons, the
        // toolbar stretched the page, and the whole page scrolled sideways
        // instead of the one row that was meant to. With one line the cross
        // size is the container's own width and the overflow stays where it
        // belongs.
        <Toolbar className="flex-col flex-nowrap items-stretch">
          <div className="flex items-center gap-2.5">
            {searchControl}
            {asGroup(refreshControl)}
          </div>
          {/* One line that scrolls, rather than a block that grows.

              This is the row that varies: a screen can put a funnel, saved
              sets, Export, the column picker and two batch menus on it, and
              wrapped they push the search up out of view and Add down off the
              bottom — the three controls somebody actually came for. Kept on
              one line they stay where they were put, and what does not fit is
              a swipe away. It is what legacy does here, and the reason its
              Activity Log sits half off the right edge.

              The padding is for the focus ring: `overflow-x` forces
              `overflow-y` to match, so a ring drawn two pixels outside a
              button would be clipped and raise a vertical scrollbar on a row
              32 pixels tall. The negative margin gives the space straight
              back, so nothing moves. */}
          {/* No bar. The global rule puts a 10px one on every scroller, and
              on a row of 32px buttons it reads as a rule drawn under half the
              toolbar. Both properties are needed: `scrollbar-width` for
              Firefox, the pseudo-element for WebKit, and the app sets each of
              them globally. Legacy clips this row the same way. */}
          <div className="-my-1 flex min-w-0 [scrollbar-width:none] items-center gap-2.5 overflow-x-auto py-1 [&::-webkit-scrollbar]:hidden">
            {filterControl}
            {filterExtraControl}
            {asGroup(
              <>
                {dataControls}
                {viewControls}
              </>,
            )}
          </div>
          {addControl ? <div className="flex justify-end">{asGroup(addControl)}</div> : null}
        </Toolbar>
      ) : tight ? (
        /* Two lines on a narrow frame. The first is the one a person came to
           use — the search, with the funnel and the reload at the end of it,
           both of which act on the search rather than on the rows. The second
           is everything else: what acts on the data and what reshapes the
           view of it, left, and Add against the right edge where nothing can
           be hit on the way to it.

           The old shape put the reload down with the actions and left the
           search line half empty, so a phone read the toolbar as a search and
           then a paragraph of buttons. Add keeps the right edge by an auto
           margin rather than by `justify-between`, so it stays there on a
           screen that has no other actions at all — and the line wraps rather
           than scrolls, because a row that scrolls is a row with Add hidden
           off the end of it. */
        <Toolbar className="flex-col items-stretch">
          <div className="flex items-center gap-2.5">
            {searchControl}
            {filterControl}
            {filterExtraControl}
            {asGroup(refreshControl)}
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            {asGroup(
              <>
                {dataControls}
                {viewControls}
              </>,
            )}
            {addControl ? (
              <div className="ml-auto flex shrink-0 items-center gap-2.5">
                {asGroup(addControl)}
              </div>
            ) : null}
          </div>
        </Toolbar>
      ) : split ? (
        <Toolbar className="flex-col items-stretch">
          <div className="flex flex-wrap items-center gap-2.5">
            {searchControl}
            {filterControl}
            {filterExtraControl}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            {actionControls}
          </div>
        </Toolbar>
      ) : (
        <Toolbar className="justify-end">
          {/* The left-hand group as one item, so the auto margin that splits the
              row sits after the last of it rather than after the search box.
              It has to be a wrapper rather than a margin on the funnel itself:
              `filterExtra` — the Saved Filters menu on Members — renders as
              `contents` and belongs inside the group, so there is nothing else
              to hang the margin on. `flex-wrap` on the group, not `nowrap`: a
              screen that puts three controls beside the search should break
              them onto a second line rather than push the actions off the
              right edge. */}
          <div className="flex min-w-0 flex-wrap items-center gap-2.5 sm:mr-auto">
            {searchControl}
            {filterControl}
            {filterExtraControl}
          </div>
          {actionControls}
        </Toolbar>
      )}

      {slow ? (
        // Only once the wait is long enough to wonder about, and only for a
        // screen reader: the rows below dim rather than disappear, which is
        // what tells a pointer the same thing without moving the table.
        <LoadingNote action={waitingFor} subject={title} />
      ) : null}

      {chips.length > 0 ? (
        // Applied filters are visible and removable here. A table that is
        // quietly hiding rows is a table that is lying to you.
        <fieldset disabled={busy} className="mb-4 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <FilterChip
              key={chip.name}
              label={chip.label}
              value={chip.value}
              onRemove={() => {
                setPage(1);
                // Removed, not blanked. A key left behind as `""` sends
                // nothing and shows no chip, so the filter reads as gone —
                // but a filters panel asking which conditions exist sees the
                // key and draws the row again, empty. Clearing one condition
                // has to leave the same state as never adding it.
                setFilterValues(({ [chip.name]: _cleared, ...rest }) => rest);
              }}
            />
          ))}
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t("rp.clearFiltersBtn")}
          </Button>
        </fieldset>
      ) : null}

      {showFilters && renderFilters
        ? renderFilters(filterValues, applyFilters, () => setShowFilters(false))
        : null}
      {showFilters && !renderFilters && filters.length ? (
        <FiltersModal
          open={showFilters}
          filters={filters}
          values={filterValues}
          onApply={(next) => {
            setFilterValues(next);
            setPage(1);
            setShowFilters(false);
          }}
          onClose={() => setShowFilters(false)}
        />
      ) : null}

      {/* No `flex-1`: with `flex: 0 1 auto` the card is as tall as its rows and
          shrinks — never grows — to fit the frame. Stretched to fill, a list of
          one employee put six hundred pixels of nothing between the row and the
          pager. Full pages look exactly as before: the card hits the frame's
          height, the table scrolls inside it, the pager sits on the bottom. */}
      <Card
        ref={card}
        style={cardFloor === undefined ? undefined : { minHeight: cardFloor }}
        className="relative flex min-h-0 flex-col overflow-hidden"
      >
        {/* A newer answer is on its way, said without taking the current one
            away. The rows used to dim to half while a re-read ran — applying a
            saved set, dropping a chip, turning a page — and a greyed table
            under a greyed toolbar reads as a screen that has been switched
            off, not one that is working. What is on screen is still the last
            true answer and still worth reading, so it keeps its contrast and
            the news goes at the card's top edge instead.

            Sweeping rather than filling: the list is one request, and a bar
            advancing against a guessed total is a bar that lies. Absolute, so
            the table does not move by two pixels every time anyone filters,
            and above the sticky header, which is the thing it sits on. */}
        {listing.isFetching && !listing.isPending ? (
          <div className="bg-accent-soft absolute inset-x-0 top-0 z-40 h-0.5 overflow-hidden">
            <div className="bg-accent animate-sweep h-full w-1/3 rounded-full" />
          </div>
        ) : null}
        {/* The table scrolls inside the card rather than the page, which is what
            lets the header stay put: `overflow-x: auto` forces `overflow-y`
            to `auto` too, so a sticky header needs this to be the scroller. The
            cap is generous, and a short table never reaches it. */}
        {/* The scroller. It was capped at a guess of how much chrome sits
            above it, which reserved height the table did not always use and left
            a band of nothing under the card. It takes exactly what is left now. */}
        {/* `relative` is load-bearing. Tailwind's `sr-only` is
            `position: absolute`, and the row menu puts one in every row. With
            no positioned ancestor they resolve against the document rather than
            this box, so row 25's screen-reader label landed at y=1400 and
            stretched <html> to 1441px — a page that scrolled through 568 pixels
            of nothing while every visible element fitted the screen. */}
        <div ref={scroller} className="table-scroller relative min-h-0 flex-1 overflow-auto">
          <table className="w-full border-separate border-spacing-0 tabular-nums">
            <thead>
              <tr>
                {mayBulk ? (
                  // The page's own box: it ticks and unticks what is on screen,
                  // which is what a person means by "all" while looking at a
                  // page. Indeterminate while some of them are ticked, so the
                  // box says "some", not "none".
                  <th
                    scope="col"
                    className="border-line bg-surface-sunken sticky top-0 z-20 w-10 border-b px-4 py-3 first:pl-5"
                  >
                    <Checkbox
                      aria-label={t("rp.selectPage")}
                      checked={pageTicked && rows.length > 0}
                      ref={(box) => {
                        if (box) box.indeterminate = !pageTicked && somePageTicked;
                      }}
                      disabled={rows.length === 0}
                      onChange={(event) => tickPage(event.target.checked)}
                    />
                  </th>
                ) : null}
                {visibleColumns.map((column) => {
                  const sorted = Boolean(column.sortKey) && sort === column.sortKey;
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={
                        sorted ? (order === "asc" ? "ascending" : "descending") : undefined
                      }
                      className={cn(
                        // Not uppercased. The reset zeroes `text-transform` on
                        // buttons, so a sortable header came out title case and
                        // its neighbours SHOUTED — and neither matched the name
                        // Customize Columns lists it under. The label as written
                        // is the one name for the column everywhere.
                        "text-fg text-subhead border-line bg-surface-sunken sticky top-0 z-20 border-b px-4 py-3 text-left font-medium whitespace-nowrap",
                        // The card's own gutter on the outside edges, so the
                        // first column starts where the card's padding would.
                        "first:pl-5 last:pr-5",
                        column.className,
                      )}
                    >
                      {column.sortKey ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => sortBy(column.sortKey ?? "")}
                          // No colour of its own: the heading is the full
                          // foreground already, and the arrow beside it — in
                          // the accent — is what says which column is sorted.
                          className={cn(
                            "group/sort -mx-1 flex w-full items-center gap-1.5 rounded px-1 transition-colors",
                            "disabled:pointer-events-none",
                          )}
                        >
                          {column.label}
                          {sorted ? (
                            order === "asc" ? (
                              <ArrowUp className="text-accent size-3.5" aria-hidden />
                            ) : (
                              <ArrowDown className="text-accent size-3.5" aria-hidden />
                            )
                          ) : (
                            <ChevronsUpDown
                              className="text-fg-subtle size-3.5 opacity-0 transition-opacity group-hover/sort:opacity-100"
                              aria-hidden
                            />
                          )}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  );
                })}
                {actions ? (
                  // Pinned to the right edge as well as the top: on a wide
                  // table the row's menu is the one cell that must stay
                  // reachable without scrolling back for it. z-30 so the
                  // corner sits over both the other headers and the cells
                  // sliding under it.
                  <th
                    scope="col"
                    className="border-line bg-surface-sunken sticky top-0 right-0 z-30 w-24 border-b border-l px-4 py-3 last:pr-5"
                  >
                    {t("rp.actions")}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody
              aria-busy={listing.isFetching || undefined}
              className="[&_tr:not(:first-child)_td]:border-line [&_tr:not(:first-child)_td]:border-t"
            >
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="group/row hover:bg-surface-sunken/70 transition-colors duration-[var(--duration-fast)]"
                >
                  {mayBulk ? (
                    <td className="px-4 py-3.5 first:pl-5">
                      <Checkbox
                        aria-label={t("rp.selectRow").replace("{row}", rowLabel(row))}
                        checked={ticked.has(row.id)}
                        onChange={() => tickOne(row.id)}
                      />
                    </td>
                  ) : null}
                  {visibleColumns.map((column) => (
                    <td
                      key={column.key}
                      // The whole row opens on a double-click where the screen
                      // asks for it. Hung on the data cells rather than the
                      // `<tr>`, so the tick box and the row menu keep their own
                      // double-clicks: a second click on a checkbox means it
                      // was ticked and unticked, not that the record should
                      // open over the top.
                      onDoubleClick={openRowOnDouble ? () => open(row) : undefined}
                      className={cn(
                        "text-fg-body text-callout px-4 py-3.5 first:pl-5 last:pr-5",
                        // The hand, not the text cursor, where a double-click
                        // opens the row: the I-beam said "select me" over a
                        // cell whose real use is opening the record.
                        openRowOnDouble && "cursor-pointer",
                        column.className,
                      )}
                    >
                      {column.key === viewColumn && !noView ? (
                        // The id is the way in: click it to see the whole record.
                        <button
                          type="button"
                          onClick={() => open(row)}
                          // `block` and bounded: a button sized to its content
                          // gives a truncating child nothing to truncate against,
                          // so a long name pushed the column wide instead.
                          // The offset matched to the one the id cells draw
                          // themselves with: without it the hover underline
                          // lands below theirs and the id grows a second line
                          // under the pointer. A cell that draws none of its
                          // own still gets one, in the same place.
                          className="text-accent block max-w-full min-w-0 rounded text-left font-medium hover:underline hover:underline-offset-4"
                        >
                          {column.render(row, lookup, shown)}
                        </button>
                      ) : (
                        column.render(row, lookup, shown)
                      )}
                    </td>
                  ))}
                  {actions ? (
                    // The card's own background, so the columns pass behind
                    // this cell rather than through it.
                    //
                    // Hover is mixed rather than tinted: one element has one
                    // background-color, so `sunken/70` would *replace* the
                    // opaque one instead of sitting over it, and the hovered
                    // row's last cell turned see-through with the table
                    // sliding past underneath. The mix is what 70% sunken over
                    // raised comes out as — the same colour the rest of the
                    // row shows, and opaque.
                    <td className="bg-surface-raised border-line sticky right-0 z-10 border-l px-2 py-2 pr-3 text-right group-hover/row:bg-[color-mix(in_oklab,var(--color-surface-sunken)_70%,var(--color-surface-raised))]">
                      <RowMenu
                        disabled={busy}
                        // In the archive the menu offers one thing. A row
                        // that has been deleted is not something to edit, and
                        // reading it is what the id cell is for.
                        onView={noView || archived ? undefined : () => open(row)}
                        onEdit={
                          readOnly || noEdit || archived || rowCanEdit?.(row, profile) === false
                            ? undefined
                            : () => setParam("edit", row.id)
                        }
                        onDelete={
                          readOnly ||
                          noDelete ||
                          // Already put away: Restore is what this row offers.
                          archived ||
                          rowCanDelete?.(row, profile) === false
                            ? undefined
                            : () => confirmDelete(row.id)
                        }
                        extra={menuFor(row, () => void invalidate())}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
              {showSkeleton
                ? Array.from({ length: 8 }, (_, index) => (
                    <tr key={index}>
                      {mayBulk ? <td className="px-4 py-3.5 first:pl-5" /> : null}
                      {visibleColumns.map((column) => (
                        <td key={column.key} className="px-4 py-3.5 first:pl-5 last:pr-5">
                          <Skeleton
                            className="h-3.5"
                            style={{
                              width: `${45 + ((index * 17 + column.key.length * 7) % 40)}%`,
                            }}
                          />
                        </td>
                      ))}
                      {actions ? (
                        // Pinned like the real one, so the column does not
                        // step sideways when the rows arrive.
                        <td className="bg-surface-raised border-line sticky right-0 z-10 border-l px-4 py-3.5 last:pr-5">
                          <Skeleton className="ml-auto size-4 rounded-full" />
                        </td>
                      ) : null}
                    </tr>
                  ))
                : null}
              {listing.isError ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length + (actions ? 1 : 0) + (mayBulk ? 1 : 0)}
                    className="p-0"
                  >
                    <EmptyState
                      kind="error"
                      title={t("rp.loadFailedTitle")}
                      // What actually went wrong, when the answer says: the
                      // server's own `detail` on a 500, "Cannot reach the
                      // server." when there was no answer at all. "Try again"
                      // is the right move for one of those and not the other,
                      // and a person cannot tell which from a generic line.
                      description={errorMessage(listing.error, t("rp.loadFailedBody"))}
                      action={
                        <Button variant="secondary" onClick={() => void listing.refetch()}>
                          <RotateCw aria-hidden /> {t("rp.retry")}
                        </Button>
                      }
                    />
                  </td>
                </tr>
              ) : null}
              {!listing.isPending && !listing.isError && rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length + (actions ? 1 : 0) + (mayBulk ? 1 : 0)}
                    className="p-0"
                  >
                    {deferredSearch || activeFilters > 0 ? (
                      <EmptyState
                        kind="search"
                        title={t("rp.noMatch").replace("{plural}", plural.toLowerCase())}
                        description={t("rp.tryAnother")}
                        action={
                          activeFilters > 0 ? (
                            <Button variant="secondary" onClick={clearFilters}>
                              {t("rp.clearFiltersBtn")}
                            </Button>
                          ) : undefined
                        }
                      />
                    ) : (
                      <EmptyState
                        title={t("rp.noneYet").replace("{plural}", plural.toLowerCase())}
                        description={
                          readOnly
                            ? t("rp.entriesAppear")
                            : t("rp.firstAdd").replace("{singular}", singular.toLowerCase())
                        }
                        action={
                          readOnly || !canAdd
                            ? undefined
                            : (addMenu ?? (
                                <Button onClick={() => setEditing("new")}>
                                  {addLabel ?? t("rp.add").replace("{singular}", singular)}
                                </Button>
                              ))
                        }
                      />
                    )}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {infinite ? (
          // No pager and no total: the next screenful comes when the table
          // reaches its end, and a log is not something one counts.
          feed.isFetchingNextPage ? (
            // Announced, not drawn: a strip under the last row saying the
            // next screenful is coming moved the rows every time one arrived.
            <LoadingNote />
          ) : null
        ) : rows.length > 0 ? (
          /* `ui/pager.tsx` draws this row for every screen that pages, the
               Yellow Page included — it used to be written out here and
               written again, differently, over there. */
          <Pager
            page={page}
            pages={pages}
            onChange={setPage}
            size={size}
            onSize={(next) => {
              setSize(next);
              setPage(1);
            }}
            total={totalLabel}
            disabled={busy}
            tight={pagerTight}
            bare={pagerBare}
            from={firstRow}
            to={lastRow}
            jumpToEnd={canJumpToEnd}
            label={title}
          />
        ) : null}
      </Card>

      {exporting && exportServer ? (
        <ServerExportDialog
          plural={plural}
          endpoint={endpoint}
          spec={exportServer}
          columns={exportColumns}
          params={params}
          totalLabel={totalLabel}
          counted={!infinite}
          onClose={() => setExporting(false)}
        />
      ) : null}

      {importing && importable ? (
        <ImportDialog
          singular={singular}
          plural={plural}
          endpoint={endpoint}
          spec={importable}
          onClose={() => setImporting(false)}
          onImported={() => void listing.refetch()}
        />
      ) : null}

      {pickingColumns ? (
        <ColumnPicker
          open
          columns={ordered}
          hidden={hidden}
          onClose={() => setPickingColumns(false)}
          onApply={(nextHidden, nextOrder) => {
            setLocalHidden(nextHidden);
            writeJson(columnsKey, nextHidden);
            setLocalOrder(nextOrder);
            writeJson(columnOrderKey, nextOrder);
            setPickingColumns(false);
            // And to the account, so the next device opens the list this way.
            // The browser's copy already took effect; a failed save is said
            // once and changes nothing on screen.
            adoptedPrefs.current = true;
            void api
              .put(listPrefsPath, { hidden: nextHidden, order: nextOrder })
              .catch((error: unknown) =>
                toast.error(errorMessage(error, t("rp.columnsNotSaved"))),
              );
          }}
        />
      ) : null}

      {formOverlay}
      {viewOverlay}
      {openingBlocker}

      {confirmDialog}
      {restoreDialog}
      {bulkDialog}
      {refusedDialog}
    </Page>
  );
}

/** `member_since_from` reads as "Member since from" — good enough for a chip. */
const humanize = (key: string) =>
  key
    .replace(/_id$/, "")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

/**
 * Re-exported for the screens and tests that have always read it from here.
 * It lives in `ui/pager.tsx` now, beside the row that uses it.
 */
export { pageWindow } from "@/components/ui/pager";

/** One extra entry, drawn the same way the built-in three are. */
function MenuAction({
  action,
  onPick,
}: {
  action: RowAction;
  onPick: (run: () => void) => () => void;
}) {
  const Icon = action.icon;
  return (
    <button type="button" onClick={onPick(action.run)} className={menuItemClass(action.danger)}>
      {Icon ? <Icon aria-hidden /> : null}
      {action.label}
    </button>
  );
}

function RowMenu({
  onView,
  onEdit,
  onDelete,
  extra = [],
  disabled = false,
}: {
  onView?: (() => void) | undefined;
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  extra?: RowAction[] | undefined;
  /** Held while the list is being read: the row may be about to change. */
  disabled?: boolean;
}) {
  const t = useTranslation();
  // A popover lives in the top layer, so the table's overflow cannot clip it,
  // and light dismiss (outside click, Esc) is the browser's.
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const place = () => {
    const anchor = button.current?.getBoundingClientRect();
    const panel = menu.current;
    if (!anchor || !panel) return;
    const width = 208;
    let left = anchor.right - width;
    let top = anchor.bottom + 6;
    if (top + panel.offsetHeight > window.innerHeight - 8)
      top = anchor.top - panel.offsetHeight - 6;
    if (left < 8) left = 8;
    Object.assign(panel.style, { top: `${top}px`, left: `${left}px`, width: `${width}px` });
  };
  const close = () => menu.current?.hidePopover();
  const pick = (action: () => void) => () => {
    close();
    action();
  };
  const before = extra.filter((action) => !action.afterDelete);
  const after = extra.filter((action) => action.afterDelete);

  return (
    <>
      <button
        ref={button}
        type="button"
        popoverTarget={id}
        disabled={disabled}
        title={t("rp.rowActions")}
        className="text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-8 place-items-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-50"
      >
        <EllipsisVertical className="size-4" aria-hidden />
        <span className="sr-only">{t("rp.rowActions")}</span>
      </button>
      <div
        ref={menu}
        id={id}
        popover="auto"
        onToggle={(event) => event.newState === "open" && place()}
        className="border-line bg-surface-raised animate-sheet fixed m-0 rounded-xl border p-1 shadow-[var(--shadow-float)]"
      >
        {onView ? (
          <button type="button" onClick={pick(onView)} className={menuItemClass()}>
            <Eye aria-hidden />
            {t("rp.view")}
          </button>
        ) : null}
        {onEdit ? (
          <button type="button" onClick={pick(onEdit)} className={menuItemClass()}>
            <Pencil aria-hidden />
            {t("common.edit")}
          </button>
        ) : null}
        {before.map((action) => (
          <MenuAction key={action.label} action={action} onPick={pick} />
        ))}
        {onDelete ? (
          <>
            {onView || onEdit || before.length ? <MenuSeparator /> : null}
            <button type="button" onClick={pick(onDelete)} className={menuItemClass(true)}>
              <Trash2 aria-hidden />
              {t("common.delete")}
            </button>
          </>
        ) : null}
        {after.length ? (
          <>
            {onView || onEdit || before.length || onDelete ? <MenuSeparator /> : null}
            {after.map((action) => (
              <MenuAction key={action.label} action={action} onPick={pick} />
            ))}
          </>
        ) : null}
      </div>
    </>
  );
}

/** Every field of the row, as the API returned it. Ids resolve to names where a filter knows them. */
const AUDIT_KEYS = ["created_at", "created_by_id", "updated_at", "updated_by_id"] as const;

/** The footnote's two lines: a stamp is a time and the person who caused it. */
const AUDIT_EVENTS = [
  { at: "created_at", by: "created_by_id", verb: "rp.stampCreated" },
  { at: "updated_at", by: "updated_by_id", verb: "rp.stampUpdated" },
] as const;

/**
 * What kind of value a field holds, said before it is read.
 *
 * Keyed off the value at hand rather than a schema, because the detail view has
 * no schema — it is handed whatever the API returned. Tones come from the
 * app's own semantic tokens, so they hold in both themes.
 */
function fieldIcon(key: string, value: unknown): { component: LucideIcon; tone: string } {
  if (key.endsWith("_by_id") || key === "manager_id")
    return { component: UserRound, tone: "text-accent" };
  if (/_at$/.test(key)) return { component: CalendarDays, tone: "text-success" };
  if (/_date$/.test(key)) return { component: CalendarDays, tone: "text-success" };
  if (key.endsWith("_id")) return { component: Link2, tone: "text-accent" };
  if (typeof value === "boolean") return { component: ToggleLeft, tone: "text-fg-subtle" };
  if (typeof value === "number") return { component: Hash, tone: "text-warning" };
  return { component: CaseSensitive, tone: "text-accent" };
}

/** Who, or when — the two things the record's footnote says. */
const AUDIT_ICON: Record<string, LucideIcon> = {
  created_at: CalendarPlus,
  created_by_id: UserRound,
  updated_at: Clock,
  updated_by_id: UserPen,
};

/**
 * The same four stamps as their own cells, which is how the modal shows them.
 *
 * A colour each, so the pair that is about creation reads apart from the pair
 * that is about the last change without either being labelled twice.
 */
const AUDIT_CELLS = [
  { key: "created_by_id", icon: UserRound, tone: "text-accent" },
  { key: "created_at", icon: CalendarPlus, tone: "text-success" },
  { key: "updated_by_id", icon: UserPen, tone: "text-warning" },
  { key: "updated_at", icon: CalendarCheck, tone: "text-danger" },
] as const;

export function DetailPanel<T extends { id: number }>({
  singular,
  screenTitle,
  row,
  lookup,
  onClose,
  onRefresh,
  busy,
  onEdit,
  onDelete,
  extra,
  hide,
  sections,
  labels,
  actions,
  variant = "panel",
}: {
  singular: string;
  /**
   * The list's own title, for the modal's "View Provinces" heading. The page
   * frame says the record's name instead, because it has a header to say it in.
   */
  screenTitle?: string | undefined;
  row: T;
  lookup: Lookup;
  onClose: () => void;
  onRefresh: () => void;
  busy: boolean;
  /** Absent when the screen or the person may not change this record. */
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  extra?: ReactNode;
  /** Shown better by `extra`, so kept out of the list above it. */
  hide?: string[] | undefined;
  sections?: { title: string; fields?: string[]; render?: (row: T) => ReactNode }[] | undefined;
  labels?: Record<string, string> | undefined;
  /**
   * "panel" takes the page over; "modal" lays the record over the list it was
   * opened from. The body is the same either way — only its frame changes, so
   * a screen reads the same record whichever way it is opened.
   */
  variant?: "panel" | "modal";
  /** The row menu's own actions, offered here too — a record read is a record acted on. */
  actions?: RowAction[] | undefined;
}) {
  const t = useTranslation();
  const record = row as unknown as Record<string, unknown>;
  // `province_name` is the label of `province_id` and reads as "Province";
  // `user_name` and `full_name` are names in their own right, and stripping the
  // suffix from those left the fields labelled "User" and "Full".
  const label = (key: string) =>
    labels?.[key] ??
    key
      .replace(/_id$/, "")
      .replace(/_name$/, (match) => (`${key.slice(0, -5)}_id` in record ? "" : match))
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  const modal = variant === "modal";
  /** An em dash on a page; the modal spells it, as the design asks. */
  const blank = <span className="text-fg-subtle">{modal ? "N/A" : "—"}</span>;
  const show = (key: string, value: unknown): ReactNode => {
    if (value === null || value === undefined || value === "") return blank;
    // An object or a list has no one-line form; `String(it)` is "[object
    // Object]". Such fields are dropped from the list below, so this is the
    // backstop for one that slips through rather than the place they are handled.
    if (typeof value === "object") return blank;
    // Every id, not just the authorship pair: the lookup already answers for
    // any declared `fk` filter and falls back to the id itself, so a club, a
    // city or a promo reads as its name wherever the screen filters on one.
    if (key.endsWith("_id"))
      return lookup(key === "updated_by_id" ? "created_by_id" : key, value as number);
    if (/_at$/.test(key) && typeof value === "string") return fmtDate(value);
    // A plain date has no time to print; `2026-08-30` is not how anyone reads one.
    if (/_date$/.test(key) && typeof value === "string")
      return format(new Date(value), "MM/dd/yyyy");
    if (typeof value === "boolean") return value ? t("rp.yes") : t("rp.no");
    if (typeof value === "number") return value.toLocaleString();
    return String(value);
  };

  // Delayed, so a re-read that lands in 80ms shows nothing rather than a
  // flicker; the dim and the held actions follow `busy` directly, because a
  // control that is live for 300ms is a control somebody can press.
  const rereading = useDelayed(busy);

  // The name leads; the id is a badge; a parent's id gives way to its name.
  // Not every table calls it `name` — an account's is `full_name`, and failing
  // that the number it signs in with.
  const titleKey = ["name", "full_name", "title", "user_name"].find(
    (key) => typeof record[key] === "string" && record[key] !== "",
  );
  const title = titleKey ? (record[titleKey] as string) : null;

  const details = Object.entries(record).filter(
    ([key, value]) =>
      key !== "id" &&
      // The page frame prints the name as its heading, so a "Name" row under it
      // would say it twice. The modal's heading says what was opened rather than
      // which row, so there the name has to stay a field — it is the first thing
      // the record says about itself.
      (modal || key !== titleKey) &&
      !hide?.includes(key) &&
      !(AUDIT_KEYS as readonly string[]).includes(key) &&
      // An id whose label is also on the record: `promo_code_id` beside
      // `promo_code`, `club_id` beside `club_name`. The name is the row; the
      // id was a second row saying "7".
      !(
        key.endsWith("_id") &&
        (`${key.slice(0, -3)}_name` in record || key.slice(0, -3) in record)
      ) &&
      // A club, a posting, a list of groups or of photos: a record within the
      // record. It reads as "[object Object]" in a row, so the screen shows it
      // through `renderDetailExtra` or not at all.
      (value === null || typeof value !== "object"),
  );

  /**
   * The fields under the headings the screen named, in that order, with
   * everything it did not name in a last untitled group. No headings at all
   * means one list, exactly as before.
   */
  const groups = (() => {
    if (!sections?.length) return [];
    const taken = new Set<string>();
    const named = sections
      .map((section) => {
        const entries = details.filter(([key]) => {
          if (!section.fields?.includes(key)) return false;
          taken.add(key);
          return true;
        });
        return { title: section.title, entries, body: section.render?.(row) };
      })
      .filter((group) => group.entries.length > 0 || group.body);
    const rest = details.filter(([key]) => !taken.has(key));
    return rest.length ? [...named, { title: "", entries: rest, body: undefined }] : named;
  })();

  /**
   * A property list: a type icon, the label, then the value in its own column,
   * two fields across.
   *
   * The icon says what kind of thing the value is before it is read — text, a
   * count, a day, a person — and it gives every row a fixed left edge, so the
   * labels and the values each line up down their own column. Two earlier tries
   * put the value at the far end of a full-width row; across a page this wide
   * that was a long way to travel for one number.
   *
   * A value too long for its column takes the whole row rather than wrapping
   * into a narrow ribbon.
   */
  const list = (entries: [string, unknown][], columns = 1) => (
    <dl className={cn("grid gap-x-12 gap-y-1", columns === 2 && "md:grid-cols-2")}>
      {entries.map(([key, value]) => {
        const Icon = fieldIcon(key, value);
        const wide = typeof value === "string" && value.length > 40;
        return (
          <div
            key={key}
            className={cn(
              "grid grid-cols-[1rem_minmax(0,10rem)_minmax(0,1fr)] items-baseline gap-x-3 py-2",
              wide && "md:col-span-2",
            )}
          >
            <Icon.component
              className={cn("size-4 shrink-0 translate-y-0.5", Icon.tone)}
              aria-hidden
            />
            <dt className="text-fg-muted text-callout truncate">{label(key)}</dt>
            <dd className="text-fg text-callout min-w-0 break-words">{show(key, value)}</dd>
          </div>
        );
      })}
    </dl>
  );

  /** The card's own padding on a page; in a modal the dialog already pads. */
  const inset = modal ? "px-0" : "px-6";

  const heading = (
    <span className="flex flex-wrap items-center gap-2.5">
      <span className="min-w-0 truncate">{title ?? `${singular} ${row.id}`}</span>
      <span className="bg-surface-inset text-fg-muted text-footnote rounded-full px-2 py-0.5 font-medium tabular-nums">
        {t("rp.id")} {row.id}
      </span>
    </span>
  );

  const actionRow = (
    // Held while the record is being re-read, like a list's toolbar. Not
    // the back link, which is outside this: leaving is always allowed.
    <fieldset disabled={busy} className="contents">
      <Button variant="secondary" aria-busy={busy || undefined} onClick={onRefresh}>
        <RotateCw className={cn(busy && "animate-spin")} aria-hidden />
        {t("rp.refresh")}
      </Button>
      {/* The same actions the row menu offers, so a record read here can
                be acted on here rather than closed and found again in the list. */}
      {actions?.map((action) => (
        <Button
          key={action.label}
          variant={action.danger ? "danger" : "secondary"}
          onClick={action.run}
        >
          {action.label}
        </Button>
      ))}
      {onDelete ? (
        // Destructive, and it should look it: it read as one more grey
        // button beside Refresh.
        <Button variant="danger" onClick={onDelete}>
          <Trash2 aria-hidden />
          {t("common.delete")}
        </Button>
      ) : null}
      {onEdit ? (
        <Button onClick={onEdit}>
          <Pencil aria-hidden />
          {t("common.edit")}
        </Button>
      ) : null}
    </fieldset>
  );

  const body = (
    <>
      {/* What the wait is for, said above the record it is about. Delayed, so a
          re-read that lands in 80ms shows nothing rather than a flicker. */}
      {rereading ? <LoadingNote /> : null}

      {/* The record's own fields, two columns wide where there is room. The
          audit trail closes the same card rather than opening a second one: it
          is a footnote about this record, not a section beside it. */}
      <section
        aria-busy={busy || undefined}
        className={cn(
          !modal && "bg-surface-raised rounded-card border-line border",
          "transition-opacity duration-[var(--duration-base)]",
          busy && "opacity-55",
        )}
      >
        {/* Grouped where the screen says how, one list where it does not. The
            record's own history stays after all of it, in the footer. */}
        {groups.length ? (
          <div className="divide-line divide-y">
            {groups.map((group) => (
              <div key={group.title} className={cn(inset, "py-5")}>
                {group.title ? (
                  <h3 className="text-fg-subtle text-eyebrow mb-3 tracking-[0.04em] uppercase">
                    {group.title}
                  </h3>
                ) : null}
                {group.body}
                {group.entries.length ? list(group.entries, modal ? 1 : 2) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className={cn(inset, "py-5")}>{list(details, modal ? 1 : 2)}</div>
        )}

        {/* Four labelled cells for two facts read as a table the record does not
            need. One line each — when, then who — is the footnote it is. */}
        <footer
          className={cn(
            "hairline-t text-subhead py-3",
            inset,
            !modal && "bg-surface-sunken/40",
          )}
        >
          {modal ? (
            // A cell each, in the order they happen: who made it, when, who
            // last changed it, when. A stamp the record does not carry is not
            // drawn, so a table with no `updated_at` shows two cells, not four
            // with half of them empty.
            <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
              {AUDIT_CELLS.filter(({ key }) => key in record).map(
                ({ key, icon: Icon, tone }) => (
                  <div key={key} className="min-w-0">
                    <dt className="text-fg-muted flex items-center gap-1.5">
                      <Icon className={cn("size-4 shrink-0", tone)} aria-hidden />
                      {label(key)}
                    </dt>
                    <dd className="text-fg mt-1 min-w-0 break-words">
                      {show(key, record[key])}
                    </dd>
                  </div>
                ),
              )}
            </dl>
          ) : (
            <dl className="text-fg-subtle grid gap-x-12 gap-y-1.5 sm:grid-cols-2">
              {AUDIT_EVENTS.filter(({ at }) => at in record).map(({ at, by, verb }) => {
                const Icon = AUDIT_ICON[at];
                return (
                  <div key={at} className="min-w-0">
                    <dt className="flex items-center gap-1.5">
                      {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden /> : null}
                      {t(verb)}
                    </dt>
                    <dd className="text-fg-muted mt-0.5 min-w-0 break-words">
                      {show(at, record[at])}
                      {record[by] ? (
                        <>
                          <span className="text-fg-subtle"> · </span>
                          {show(by, record[by])}
                        </>
                      ) : null}
                    </dd>
                  </div>
                );
              })}
            </dl>
          )}
        </footer>
      </section>

      {extra ? (
        <div
          className={cn(
            "mt-4",
            !modal && "bg-surface-raised rounded-card border-line border px-5 py-4",
          )}
        >
          {extra}
        </div>
      ) : null}
    </>
  );

  if (modal) {
    return (
      <Modal
        open
        onClose={onClose}
        // Wide: a label column and a value column, each with room to say itself.
        size="lg"
        // "View Provinces" — what was opened, not which row. The record names
        // itself in its own first field, so a title repeating it says nothing.
        title={`${t("rp.view")} ${screenTitle ?? singular}`}
        // The body is the record's own rows; without a rule the heading reads
        // as the first of them.
        titleDivider
        // No Refresh, Edit or Delete: a sheet over the list is for reading one
        // record, and every one of those actions is a click away in the row it
        // was opened from. The X is the only thing a reader needs here.
        footer={null}
      >
        {body}
      </Modal>
    );
  }

  return (
    <>
      <PageHeader
        back={
          <button type="button" onClick={onClose} className={backLinkClass}>
            <BackArrow label={singular} />
          </button>
        }
        eyebrow={singular}
        title={heading}
        actions={actionRow}
      />
      {body}
    </>
  );
}

/**
 * Export: the columns ticked here, every row that matches, written by the
 * server.
 *
 * The legacy export asked the same question before it wrote anything — which
 * columns, and it is the whole result rather than the page — and asking is
 * what makes the answer usable: a place list has ten columns, and the file
 * somebody wants is rarely all ten.
 */
function ServerExportDialog({
  plural,
  endpoint,
  spec,
  columns,
  params,
  totalLabel,
  counted,
  onClose,
}: {
  plural: string;
  endpoint: string;
  spec: NonNullable<ResourceConfig<{ id: number }>["exportServer"]>;
  columns: { key: string; label: string; fields: string[]; selected: boolean }[];
  params: ListParams;
  /** Already shaped for reading — "10,000+" when the count stopped at the cap. */
  totalLabel: string;
  /** Whether the list has a total to quote. An `infinite` one has none. */
  counted: boolean;
  onClose: () => void;
}) {
  const t = useTranslation();
  /** What the screen is showing is the right default; this is for the file
   *  that wants less than a person reading does, or the one column nobody
   *  needs on screen but the file does. */
  const [picked, setPicked] = useState<string[]>(() =>
    columns.filter((column) => column.selected).map((column) => column.key),
  );
  const [error, setError] = useState<string | null>(null);
  const all = picked.length === columns.length;

  const fields = [
    ...new Set(
      columns
        .filter((column) => picked.includes(column.key))
        .flatMap((column) => column.fields),
    ),
  ];

  const download = useMutation({
    mutationFn: async () => {
      // The list's own question, without the page: an export is every match.
      const { page: _page, size: _size, ...filters } = params;
      const file = await api.post<Blob>(
        `${endpoint}/${spec.path ?? "export"}`,
        { ...filters, fields },
        { responseType: "blob" },
      );
      downloadBlob(file.data, csvName(endpoint.replace(/^\//, "")));
    },
    onSuccess: onClose,
    onError: (err) => setError(errorMessage(err, t("rp.exportFailed"))),
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t("rp.exportTitle").replace("{plural}", plural)}
      // A list that scrolls back through time never counts, so there is no
      // number to quote — and quoting the `0` that stands in for "not counted"
      // would read as an empty file.
      description={
        counted
          ? t("rp.exportBody")
              .replace("{total}", totalLabel)
              .replace("{plural}", plural.toLowerCase())
          : t("rp.exportBodyUncounted")
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => download.mutate()}
            disabled={picked.length === 0}
            loading={download.isPending}
            loadingLabel={t("rp.exporting")}
          >
            {t("rp.export")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <label className="hover:bg-surface-sunken flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2">
          <Checkbox
            checked={all}
            onChange={(event) =>
              setPicked(event.target.checked ? columns.map((column) => column.key) : [])
            }
          />
          <span className="text-fg text-callout font-medium">{t("rp.allColumns")}</span>
        </label>
        <ul className="border-line grid max-h-72 gap-1 overflow-y-auto rounded-lg border p-1">
          {columns.map((column) => (
            <li key={column.key}>
              <label className="hover:bg-surface-sunken flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2">
                <Checkbox
                  checked={picked.includes(column.key)}
                  onChange={(event) =>
                    setPicked((keys) =>
                      event.target.checked
                        ? [...keys, column.key]
                        : keys.filter((key) => key !== column.key),
                    )
                  }
                />
                <span className="text-fg text-callout">{column.label}</span>
              </label>
            </li>
          ))}
        </ul>
        {error ? (
          <p
            role="alert"
            className="bg-danger-soft text-danger text-callout rounded-lg px-3 py-2.5"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Import: a file in, a count and a list of what would not go, out.
 *
 * The legacy system's import staged a file into a grid, let it be edited, and
 * wrote what survived. The part that carries its weight is the reporting —
 * every rejected row named with its line and its reason, the rest written
 * anyway — so that is what this keeps: pick the file, see what happened, fix
 * those lines in the spreadsheet and send it again.
 */
function ImportDialog({
  singular,
  plural,
  endpoint,
  spec,
  onClose,
  onImported,
}: {
  singular: string;
  plural: string;
  endpoint: string;
  spec: NonNullable<ResourceConfig<{ id: number }>["importable"]>;
  onClose: () => void;
  onImported: () => void;
}) {
  const t = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  /** The file's own header, to say which column is missing before sending it. */
  const [header, setHeader] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  /** Whether anything was written, so closing knows the list behind it is stale. */
  const landed = useRef(false);

  const missing = header ? missingColumns(header, spec.columns) : [];

  const close = () => {
    if (landed.current) onImported();
    onClose();
  };

  const take = async (picked: File | null) => {
    setResult(null);
    setError(null);
    setFile(picked);
    setHeader(null);
    if (!picked) return;
    // Only the first line is read: the check is "does this file have the
    // columns", and a megabyte of rows answers nothing the header cannot.
    const text = await picked.text();
    setHeader(parseCsv(text)[0] ?? []);
  };

  const send = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append("file", file!);
      const { data } = await api.post<ImportResult>(
        `${endpoint}/${spec.path ?? "import"}`,
        body,
      );
      return data;
    },
    onSuccess: (data) => {
      setResult(data);
      landed.current = landed.current || data.created > 0;
      if (data.created > 0) {
        toast.success(
          t("rp.importedCount")
            .replace("{count}", String(data.created))
            .replace("{plural}", (data.created === 1 ? singular : plural).toLowerCase()),
        );
        onImported();
      }
    },
    onError: (err) => setError(errorMessage(err, t("rp.importFailed"))),
  });

  return (
    <Modal
      open
      onClose={close}
      size="lg"
      title={t("rp.importTitle").replace("{plural}", plural)}
      description={t("rp.importHint").replace("{columns}", spec.columns.join(", "))}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {result ? t("common.close") : t("common.cancel")}
          </Button>
          <Button
            onClick={() => send.mutate()}
            disabled={!file || missing.length > 0}
            loading={send.isPending}
            loadingLabel={t("rp.importing")}
          >
            {t("rp.import")}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Button asChild variant="secondary" size="sm" className="justify-self-start">
          {/* A plain link to a file that exists. No handler, no blob, no
              scripted click — the browser does what it does with a link. */}
          <a href={spec.template} download={spec.file}>
            <Download aria-hidden />
            {t("rp.template")}
          </a>
        </Button>

        <label className="grid gap-1.5">
          <span className="text-fg text-subhead font-medium">{t("rp.chooseFile")}</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => void take(event.target.files?.[0] ?? null)}
            className={cn(
              controlClass,
              "h-auto cursor-pointer px-3 py-2",
              "file:bg-surface-inset file:text-fg file:text-footnote file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:px-2.5 file:py-1.5",
            )}
          />
        </label>

        {missing.length > 0 ? (
          <p
            role="alert"
            className="bg-danger-soft text-danger text-callout rounded-lg px-3 py-2.5"
          >
            {t("rp.importMissing").replace("{columns}", missing.join(", "))}
          </p>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="bg-danger-soft text-danger text-callout rounded-lg px-3 py-2.5"
          >
            {error}
          </p>
        ) : null}

        {result ? (
          <div className="grid gap-2">
            <p className="text-callout text-heading font-semibold">
              {t("rp.importedCount")
                .replace("{count}", String(result.created))
                .replace("{plural}", (result.created === 1 ? singular : plural).toLowerCase())}
            </p>
            {result.skipped.length > 0 ? (
              <>
                {/* Every rejected row, with the line to go back to. The file is
                    not re-sent from here: the fix belongs in the spreadsheet,
                    where the other rows are. */}
                <p className="text-fg-muted text-subhead">
                  {t("rp.importSkipped").replace("{count}", String(result.skipped.length))}
                </p>
                <ul className="border-line max-h-64 overflow-y-auto rounded-lg border">
                  {result.skipped.map((bad) => (
                    <li
                      key={`${bad.row}-${bad.name ?? ""}`}
                      className="border-line text-callout flex gap-3 border-b px-3 py-2 last:border-b-0"
                    >
                      <span className="text-fg-subtle tabular-nums">
                        {t("rp.importLine").replace("{line}", String(bad.row))}
                      </span>
                      <span className="min-w-0">
                        {bad.name ? <span className="font-medium">{bad.name}: </span> : null}
                        {bad.message}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/** One row a bulk action would not move. */
interface BulkRefusal {
  id: number;
  name?: string | null;
  message: string;
}

/** What the bulk archive and restore routes answer with. */
interface BulkAnswer {
  done: number;
  refused: BulkRefusal[];
}

/** What ``POST /{places}/import`` answers with. */
interface ImportResult {
  created: number;
  skipped: { row: number; name?: string | null; column?: string | null; message: string }[];
}

/**
 * The columns in the order somebody put them, by key.
 *
 * A stable sort on rank, so anything the saved order does not name keeps its
 * declared position — at the end, which is where a column added to the screen
 * since belongs until somebody moves it.
 */
function orderColumns<T>(columns: ColumnDef<T>[], order: string[]): ColumnDef<T>[] {
  if (order.length === 0) return columns;
  const rank = new Map(order.map((key, index) => [key, index]));
  return [...columns].sort(
    (a, b) => (rank.get(a.key) ?? Infinity) - (rank.get(b.key) ?? Infinity),
  );
}

/**
 * Customize Columns: which columns the table shows, and in what order.
 *
 * A drawer rather than a sheet over the middle, for the same reason the
 * advanced filter is one: the answer to "is Employee Number in here?" is the
 * table itself, and a drawer leaves it on screen to be checked against while
 * the switches are flicked.
 *
 * In table order, not A–Z: the list is the table's own running order now, so
 * sorting it by label would show a row three places from where dragging it
 * would put it. Applied on OK, both of them, so a drag is as undoable as a
 * switch until then.
 */
function ColumnPicker<T>({
  open,
  columns,
  hidden,
  onClose,
  onApply,
}: {
  open: boolean;
  columns: ColumnDef<T>[];
  hidden: string[];
  onClose: () => void;
  onApply: (hidden: string[], order: string[]) => void;
}) {
  const t = useTranslation();
  // Mounted only while open, so both drafts start from what is on screen every
  // time without an effect to reset them.
  const [draft, setDraft] = useState<string[]>(hidden);
  const [order, setOrder] = useState<string[]>(() => columns.map((column) => column.key));
  /** The row being dragged, which is also what says a drag is under way. */
  const [dragging, setDragging] = useState<string | null>(null);
  /**
   * How many columns the table would show, counted off the columns themselves.
   *
   * Not `columns.length - draft.length`: the hidden list is a set of keys a
   * browser has been carrying since whenever, and it can name a column this
   * screen no longer has, or name one twice. Either made the count read low,
   * and the count is what decides when the last switch locks — so a list with
   * eight columns showing would refuse to turn the ninth off, and every switch
   * still on looked disabled at once.
   */
  const shown = columns.filter((column) => !draft.includes(column.key)).length;
  const listed = order
    .map((key) => columns.find((column) => column.key === key))
    .filter((column): column is ColumnDef<T> => Boolean(column));

  /** Put `key` where `before` is, which is what a drag over a row means. */
  const move = (key: string, before: string) =>
    setOrder((keys) => {
      const from = keys.indexOf(key);
      const to = keys.indexOf(before);
      if (from < 0 || to < 0 || from === to) return keys;
      const next = [...keys];
      next.splice(to, 0, ...next.splice(from, 1));
      return next;
    });
  /** The same move by keyboard, which a pointer-only reorder leaves out. */
  const nudge = (key: string, delta: number) =>
    setOrder((keys) => {
      const from = keys.indexOf(key);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= keys.length) return keys;
      const next = [...keys];
      next.splice(to, 0, ...next.splice(from, 1));
      return next;
    });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      // A switch, a handle and a column's name is a short row, and thirty of
      // them are thirty short rows: at `md` the list ran down the left of the
      // sheet with the right half of it empty. Sized to what is in it instead,
      // so a table of long column names still gets the room it needs and one
      // of "ID", "Name", "Club" does not get four hundred pixels for it.
      width="auto"
      title={t("rp.customizeColumn")}
      description={t("rp.showHide")}
      footer={
        // One line, both verbs, half the width each — the legacy drawer's
        // footer, where Cancel and OK are the same size.
        <div className="flex w-full gap-2.5">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            className="flex-1"
            onClick={() => onApply(draft, order)}
            disabled={shown === 0}
          >
            {t("rp.ok")}
          </Button>
        </div>
      }
    >
      <ul className="grid gap-0.5">
        {listed.map((column) => {
          const on = !draft.includes(column.key);
          return (
            <li
              key={column.key}
              draggable
              onDragStart={(event) => {
                setDragging(column.key);
                event.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => setDragging(null)}
              onDragOver={(event) => {
                // Without this the drop is refused and the row springs back.
                event.preventDefault();
                if (dragging && dragging !== column.key) move(dragging, column.key);
              }}
              onDrop={(event) => event.preventDefault()}
              className={cn(
                "hover:bg-surface-sunken flex items-center gap-3 rounded-lg px-2 py-1.5",
                dragging === column.key && "bg-surface-sunken opacity-60",
              )}
            >
              {/* The handle is what says the row moves at all. Arrow keys do the
                  same thing, because a reorder that needs a pointer is a
                  reorder half the people using this cannot make. */}
              <span
                role="button"
                tabIndex={0}
                aria-label={t("rp.reorderColumn").replace("{column}", column.label)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
                  event.preventDefault();
                  nudge(column.key, event.key === "ArrowUp" ? -1 : 1);
                }}
                className="text-fg-subtle hover:text-fg grid size-6 shrink-0 cursor-grab place-items-center rounded active:cursor-grabbing"
              >
                <GripVertical className="size-4" aria-hidden />
              </span>
              <Toggle
                checked={on}
                aria-label={column.label}
                // Small and in the accent: thirty of these are a list of what
                // the table shows, not thirty settings, and green here would
                // read as approval of the column rather than its state.
                size="sm"
                tone="accent"
                // The last visible column cannot be turned off: a table with
                // no columns is not a smaller table, it is a broken one.
                disabled={on && shown === 1}
                onChange={(event) =>
                  setDraft((held) =>
                    event.target.checked
                      ? held.filter((key) => key !== column.key)
                      : [...held, column.key],
                  )
                }
              />
              <span className="text-fg text-callout min-w-0 flex-1 truncate">
                {column.label}
              </span>
            </li>
          );
        })}
      </ul>
    </Drawer>
  );
}

/** Filters as a dialog: a draft the person can change freely, applied on one click. */
/**
 * A range is two filters that are one question.
 *
 * `created_from`/`created_to` and `min_population`/`max_population` were two
 * rows apiece, each labelled with half a thought — "Created from", then
 * "Created to" — so a drawer of five questions read as eight controls. Paired,
 * the stem is the label and the two ends sit side by side under it.
 */
function rangePair(filter: FilterDef, next: FilterDef | undefined) {
  if (!next) return null;
  const pairs: [RegExp, RegExp][] = [
    [/_from$/, /_to$/],
    [/^min_/, /^max_/],
  ];
  for (const [head, tail] of pairs) {
    if (!head.test(filter.name) || !tail.test(next.name)) continue;
    const stem = filter.name.replace(head, "").replace(/^_|_$/g, "");
    if (next.name.replace(tail, "").replace(/^_|_$/g, "") !== stem) continue;
    return stem;
  }
  return null;
}

/** Which end of a range a field is: "From"/"To", or "Minimum"/"Maximum". */
function rangeEnd(name: string): string {
  if (name.endsWith("_from")) return "From";
  if (name.endsWith("_to")) return "To";
  if (name.startsWith("min_")) return "Minimum";
  if (name.startsWith("max_")) return "Maximum";
  return "";
}

function FiltersModal({
  open,
  filters,
  values,
  onApply,
  onClose,
}: {
  open: boolean;
  filters: FilterDef[];
  /** Every field here is one value; the type is the page's, so it is the union. */
  values: FilterValues;
  onApply: (values: FilterValues) => void;
  onClose: () => void;
}) {
  const t = useTranslation();
  // Mounted only while open, so this starts from the applied filters each time.
  const [draft, setDraft] = useState(values);

  const set = (name: string, value: string) =>
    setDraft((current) => {
      const next = { ...current, [name]: value };
      // A new parent invalidates whatever child was chosen under the old one.
      for (const child of filters) if (child.dependsOn === name) next[child.name] = "";
      return next;
    });

  const at = (values: FilterValues, name: string) => String(values[name] ?? "");
  const chosen = filters.filter((filter) => at(draft, filter.name) !== "").length;
  // Nothing to apply until something differs from what is already applied — the
  // button used to invite a round trip that could only return the same rows.
  const changed = filters.some((filter) => at(draft, filter.name) !== at(values, filter.name));

  // Ranges take both of their halves, so the second one is not drawn again.
  const paired = new Set<string>();
  const rows: { key: string; label: string; parts: FilterDef[] }[] = [];
  filters.forEach((filter, index) => {
    if (paired.has(filter.name)) return;
    const next = filters[index + 1];
    const stem = rangePair(filter, next);
    if (stem && next) {
      paired.add(next.name);
      rows.push({ key: filter.name, label: humanize(stem), parts: [filter, next] });
    } else {
      rows.push({ key: filter.name, label: filter.label, parts: [filter] });
    }
  });

  return (
    // A modal that is as tall as its questions, not a sheet down the whole edge
    // of the screen: five filters left two thirds of a full-height drawer
    // empty, and put the Apply button a long way from the last field.
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-baseline gap-2">
          {t("rp.filters")}
          {chosen > 0 ? (
            <span className="bg-accent-soft text-accent text-footnote rounded-full px-2 py-0.5 font-medium tabular-nums">
              {chosen}
            </span>
          ) : null}
        </span>
      }
      footer={
        <>
          <Button
            variant="ghost"
            className="mr-auto"
            disabled={chosen === 0}
            onClick={() => setDraft({})}
          >
            {t("rp.clear")}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={!changed} onClick={() => onApply(draft)}>
            {t("rp.applyFilter")}
          </Button>
        </>
      }
    >
      {/* One question per line, so the modal reads straight down; a range still
          keeps its two ends side by side inside its own row. */}
      <div className="grid gap-4 pb-3">
        {rows.map((row) => {
          const active = row.parts.some((part) => at(draft, part.name) !== "");
          return (
            <div key={row.key} className="grid gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-fg text-subhead font-medium">{row.label}</span>
                {/* One filter at a time, without wiping the other four. */}
                {active ? (
                  <button
                    type="button"
                    onClick={() => row.parts.forEach((part) => set(part.name, ""))}
                    className="text-fg-subtle hover:text-accent text-footnote"
                  >
                    {t("rp.clear")}
                  </button>
                ) : null}
              </div>
              {/* The two ends of a range side by side, each captioned, so the
                  pair reads as one question rather than two rows that happen to
                  share a heading. */}
              <div className={cn(row.parts.length === 2 && "grid grid-cols-2 gap-2")}>
                {row.parts.map((part) => (
                  <div key={part.name} className="grid gap-1">
                    {row.parts.length === 2 ? (
                      <span className="text-fg-muted text-footnote">{rangeEnd(part.name)}</span>
                    ) : null}
                    <FilterControl
                      filter={part}
                      value={at(draft, part.name)}
                      parent={part.dependsOn ? at(draft, part.dependsOn) : ""}
                      onChange={(value) => set(part.name, value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function FilterControl({
  filter,
  value,
  parent,
  onChange,
}: {
  filter: FilterDef;
  value: string;
  parent: string;
  onChange: (value: string) => void;
}) {
  const t = useTranslation();
  const waiting = Boolean(filter.dependsOn) && parent === "";
  const options = useQuery({
    queryKey: ["options", filter.name, parent],
    queryFn: ({ signal }) =>
      filter.loadOptions?.(parent, signal) ?? Promise.resolve(filter.options ?? []),
    enabled: filter.type === "select" && !waiting,
    staleTime: 5 * 60_000,
  });

  return (
    <label className="grid gap-1.5">
      {/* A `<label>` names the control it wraps by its own text. The visible
          text moved to the row above, which names nothing — a label is not a
          widget, and `aria-label` on one does not reach the control inside — so
          the name is carried here. For a range this is also the only thing that
          says which end you are in. */}
      <span className="sr-only">{filter.label}</span>
      {filter.type === "fk" ? (
        <FkSelect
          endpoint={filter.endpoint ?? ""}
          value={value}
          onChange={(next) => onChange(next)}
          params={
            filter.dependsOn ? { [filter.parentParam ?? filter.dependsOn]: parent } : undefined
          }
          disabled={waiting}
          placeholder={
            waiting
              ? t("rp.chooseFirst").replace(
                  "{parent}",
                  filter.dependsOn?.replace(/_id$/, "") ?? "",
                )
              : t("rp.selectDots")
          }
        />
      ) : filter.type === "date" ? (
        // The app's own calendar, as the forms use: the native one is the
        // operating system's control, a different panel on every platform.
        <DatePicker value={value} onChange={onChange} />
      ) : filter.type === "number" || filter.type === "text" ? (
        <Input
          type={filter.type}
          inputMode={filter.type === "number" ? "numeric" : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        // Ours, not the operating system's: a native <select> opens the OS menu,
        // which on macOS is dark grey with its own type and its own blue — the
        // one control that fell out of the app the moment it was opened.
        <OptionSelect
          value={value}
          onChange={onChange}
          disabled={waiting}
          placeholder={
            waiting
              ? t("rp.chooseFirst").replace(
                  "{parent}",
                  filter.dependsOn?.replace(/_id$/, "") ?? "",
                )
              : t("rp.selectDots")
          }
          choices={
            filter.type === "boolean"
              ? [
                  { value: "true", label: t("rp.yes") },
                  { value: "false", label: t("rp.no") },
                ]
              : (options.data ?? []).map((option) => ({
                  value: String(option.value),
                  label: option.label,
                }))
          }
        />
      )}
    </label>
  );
}

/**
 * One property, in the shape the detail view draws every other one: the type
 * icon, the label, then the value in its own column.
 *
 * Exported so a section that draws itself — a club and its posting, a role —
 * does not end up looking like a different screen three rows down.
 */
export function DetailProperty({
  label,
  icon,
  children,
}: {
  label: string;
  /** Defaults to the text icon; pass one where the value is not text. */
  icon?: LucideIcon;
  children: ReactNode;
}) {
  const Icon = icon ?? CaseSensitive;
  return (
    <div className="grid grid-cols-[1rem_minmax(0,10rem)_minmax(0,1fr)] items-baseline gap-x-3 py-2">
      <Icon className="text-accent size-4 shrink-0 translate-y-0.5" aria-hidden />
      <dt className="text-fg-muted text-callout truncate">{label}</dt>
      <dd className="text-fg text-callout min-w-0 break-words">{children}</dd>
    </div>
  );
}

/**
 * The chrome a create or edit wears when it takes over the page: a way back, a
 * title, and the form in a card that runs the page's width.
 *
 * Exported because three screens cannot express their form as a `FieldDef[]` —
 * an employee number that gates the rest of the form, a listing's photo tray —
 * and they should still be indistinguishable from the ones that can.
 */
export function FormPanel({
  title,
  onClose,
  actions,
  children,
}: {
  title: string;
  onClose: () => void;
  actions: ReactNode;
  children: ReactNode;
}) {
  const t = useTranslation();
  return (
    <section>
      <button
        type="button"
        onClick={onClose}
        className="text-fg-muted hover:text-fg text-callout inline-flex items-center gap-2"
      >
        <ChevronLeft className="size-4" aria-hidden /> {t("rp.back")}
      </button>
      <h2 className="text-title-2 text-heading mt-3">{title}</h2>
      {/* In a card, like the role editor's, and taking the page's width: the
          fields were a single narrow column with the right half of the screen
          left blank beside them. */}
      <div className="bg-surface-raised rounded-card mt-5 p-5 shadow-[var(--shadow-lift)]">
        {children}
        <div className="mt-6 flex items-center justify-end gap-3">{actions}</div>
      </div>
    </section>
  );
}

/**
 * Fills each `employeeLookup` field's partner with the name behind the number.
 *
 * Debounced, because it runs on every keystroke of a number being typed, and
 * abandoned when the number changes again before the answer lands. A number
 * that names nobody empties the name rather than leaving the last one
 * standing beside a different number.
 */
function useEmployeeNames(
  fields: FieldDef[],
  values: Record<string, string>,
  setValues: (update: (current: Record<string, string>) => Record<string, string>) => void,
) {
  const lookups = fields.filter((field) => field.employeeLookup);
  // One string of everything watched: the effect re-runs when any number does,
  // and an array of fields rebuilt every render would re-run it always.
  const watched = lookups.map((field) => `${field.name}=${values[field.name] ?? ""}`).join("&");

  useEffect(() => {
    const pairs = watched
      .split("&")
      .filter(Boolean)
      .map((entry) => entry.split("="));
    const controller = new AbortController();
    const timer = setTimeout(() => {
      for (const [name, number] of pairs) {
        const fills = fields.find((field) => field.name === name)?.employeeLookup;
        if (!fills) continue;
        const typed = (number ?? "").trim();
        if (!typed) {
          setValues((current) => (current[fills] ? { ...current, [fills]: "" } : current));
          continue;
        }
        void api
          .get<{ full_name: string }>("/users/by-employee-number", {
            params: { employee_number: typed },
            signal: controller.signal,
          })
          .then(({ data }) => setValues((current) => ({ ...current, [fills]: data.full_name })))
          // No such employee is an answer, not a failure: the name empties and
          // the form says nothing. The save is what refuses a bad number.
          .catch(() =>
            setValues((current) => (current[fills] ? { ...current, [fills]: "" } : current)),
          );
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `fields` is rebuilt on every render of the page above; `watched` is the
    // part of it this depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched]);
}

export function EditDialog({
  singular,
  endpoint,
  fields,
  initial,
  id,
  stamp = null,
  onClose,
  onSaved,
  columns = 1,
  variant = "modal",
  fixed,
  action,
}: {
  singular: string;
  endpoint: string;
  fields: FieldDef[];
  initial: Record<string, string>;
  id: number | null;
  /**
   * A sub-route of the record to PATCH — `basic-info` — for a screen that edits
   * one card of a record at a time, each card its own endpoint.
   */
  action?: string | undefined;
  /** The record's `updated_at` as read, sent back so a save over someone else's is refused. */
  stamp?: string | null | undefined;
  onClose: () => void;
  /** Given the saved record, for a caller that has to point at what it made. */
  onSaved: (saved: { id: number }) => void;
  columns?: 1 | 2;
  /**
   * "modal" is a sheet over the screen; "panel" takes the place of the list it
   * was opened from, the way the role editor does. Same form either way — only
   * the chrome around it differs.
   */
  variant?: "modal" | "panel";
  /**
   * Sent with every save but never shown: the parent a row belongs to. A
   * reason's kind is decided by the list it was opened from, so it is neither
   * a field to fill in nor something to leave out of the body.
   */
  fixed?: Record<string, unknown>;
}) {
  const t = useTranslation();
  // The footer's submit button reaches the form across the modal by this id.
  // Unique per dialog, because a field's + opens a second one over this one,
  // and a shared literal would point both footers at whichever rendered first.
  const formId = useId();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.name, initial[field.name] ?? ""])),
  );
  const [error, setError] = useState<string | null>(null);
  useEmployeeNames(fields, values, setValues);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id === null
        ? createResource<{ id: number }>(endpoint, body)
        : updateResource<{ id: number }>(endpoint, id, body, stamp, action),
    onSuccess: (saved) => {
      toast.success(
        t(id === null ? "rp.created" : "rp.updated").replace("{singular}", singular),
      );
      onSaved(saved);
    },
    onError: (err) =>
      setError(
        errorMessage(err, t("rp.saveFailed").replace("{singular}", singular.toLowerCase())),
      ),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    // Numbers go over as numbers; a cleared optional field goes over as null
    // so an update can actually unset it. A select's value is a number only
    // when it looks like one — an id — and a word otherwise: `gender` is
    // "female", and `Number("female")` is NaN, which JSON writes as null.
    const body = Object.fromEntries(
      // `showOnly` fields are on the form to be read, not to be written. See
      // the flag: sending one is an edit of it, whatever it holds.
      fields
        .filter((field) => !field.showOnly)
        .map((field) => {
          const raw = values[field.name] ?? "";
          if (field.type === "boolean") return [field.name, raw === "true"];
          if (raw === "") return [field.name, null];
          const numeric =
            field.type === "number" || (field.type === "select" && /^-?\d+$/.test(raw));
          return [field.name, numeric ? Number(raw) : raw];
        }),
    );
    save.mutate({ ...body, ...fixed });
  };

  const title = t(id === null ? "rp.addSingular" : "rp.editSingular").replace(
    "{singular}",
    singular,
  );
  const actions = (
    <>
      <Button type="button" variant="secondary" onClick={onClose}>
        {t("common.cancel")}
      </Button>
      <Button
        type="submit"
        form={formId}
        loading={save.isPending}
        loadingLabel={t("common.saving")}
      >
        {t("rp.save")}
      </Button>
    </>
  );

  // A panel has the page's width, so its fields run two across whatever the
  // caller asked for; the section headings and textareas need to know too.
  const across = columns === 2 || variant === "panel" ? 2 : 1;
  const body = (
    <Form
      id={formId}
      onSubmit={submit}
      /**
       * Enter belongs to the field it is pressed in, not to the form.
       *
       * A form with a submit button submits on Enter from any of its inputs,
       * which turns a half-filled record into a save nobody asked for — and on
       * a form of this length, Enter is what people press to mean "yes, that
       * one" or "open this". Saving is the footer's button. Buttons and
       * textareas keep Enter's own meaning: a picker opens, a paragraph takes
       * a new line.
       */
      onKeyDown={(event) => {
        if (event.key !== "Enter" || event.shiftKey) return;
        const tag = (event.target as HTMLElement).tagName.toLowerCase();
        if (tag === "button" || tag === "textarea") return;
        event.preventDefault();
      }}
      className={cn("grid gap-4 pb-2", across === 2 && "md:grid-cols-2 md:items-start")}
    >
      {/* The error sits at the top of the form, where the eye goes when a
            save fails, rather than at the bottom under the fold.

          It spans the columns only where there are two of them. A one-column
          form is a grid with no template — one implicit column — and a child
          told to span two *makes* the second: the grid widens and every field
          after it is placed two across, so a refused save re-laid the form out
          under the person reading why. */}
      {error ? (
        <p
          role="alert"
          className={cn(
            "bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5",
            across === 2 && "md:col-span-2",
          )}
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}

      {fields.map((field, index) => (
        <FieldInput
          key={field.name}
          field={field}
          editing={id !== null}
          columns={across}
          sectionStart={
            field.section !== undefined && field.section !== fields[index - 1]?.section
          }
          value={values[field.name] ?? ""}
          parent={field.dependsOn ? (values[field.dependsOn] ?? "") : ""}
          onChange={(value) =>
            setValues((current) => {
              const next = { ...current, [field.name]: value };
              // A new parent invalidates whatever child was chosen under the old one.
              for (const child of fields)
                if (child.dependsOn === field.name) next[child.name] = "";
              return next;
            })
          }
        />
      ))}
    </Form>
  );

  if (variant === "panel") {
    return (
      <FormPanel title={title} onClose={onClose} actions={actions}>
        {body}
      </FormPanel>
    );
  }

  // One `Modal` for the whole of it. The skeleton and the form are the same
  // element in the same position, so React changes the children and leaves the
  // dialog alone — no second sheet, and nothing to flash however long the read
  // takes.
  return (
    <Modal
      open
      onClose={onClose}
      size={columns === 2 ? "lg" : "md"}
      title={title}
      footer={
        // Half the width each on a phone, where the sheet rises from the
        // bottom edge and is as wide as the screen: two buttons huddled
        // against its right corner make the thumb reach across Cancel to get
        // to Save. A two-track grid rather than `flex-1` on each button — the
        // buttons carry `shrink-0`, and a grid gives them equal halves without
        // the caller having to undo that.
        //
        // From `sm` up it is a centred dialog no wider than the content, and
        // stretching two buttons across it only makes them look like a mobile
        // sheet on a desktop. The row goes back to what it was: both buttons
        // their own size, against the right edge. The breakpoint is the one
        // `PHONE` uses, so the layout changes exactly where the shape does.
        <div className="grid w-full grid-cols-2 gap-2.5 sm:flex sm:w-auto sm:gap-2">
          {actions}
        </div>
      }
    >
      {body}
    </Modal>
  );
}

/**
 * A picker with a + beside it that makes the row it could not find.
 *
 * The dialog is portalled out of the form rather than rendered where the field
 * sits: a `<form>` inside a `<form>` is not a tree HTML allows, and the second
 * dialog carries one. Out at the body it is a sibling, and the browser stacks
 * the two modals for us.
 */
function RelatedCreateField({
  create,
  endpoint,
  disabled,
  onCreated,
  children,
}: {
  create: RelatedCreate;
  /** The picker's `/compact` endpoint, whose options the new row invalidates. */
  endpoint: string;
  disabled: boolean;
  onCreated: (id: string) => void;
  children: ReactNode;
}) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  if (create.permission && !allow(create.permission)) return <>{children}</>;
  return (
    /* `items-start`, and a button the same height as the control it stands
       beside. The field scale runs 32 / 40 / 44 and the button scale tops out
       at 32, so the default came up eight pixels short of a `md` picker and
       sat high against it — one control and a smaller square floating near its
       top edge, rather than a pair. `shrink-0` because the flex row would
       otherwise take the difference out of the button before the picker. */
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">{children}</div>
      <IconButton
        variant="secondary"
        size="icon-md"
        className="shrink-0"
        label={t("rp.addSingular").replace("{singular}", create.singular)}
        disabled={disabled}
        onClick={() => setAdding(true)}
      >
        <Plus aria-hidden />
      </IconButton>
      {adding
        ? createPortal(
            <EditDialog
              singular={create.singular}
              endpoint={create.endpoint}
              fields={create.fields}
              columns={2}
              initial={{}}
              id={null}
              onClose={() => setAdding(false)}
              onSaved={(saved) => {
                setAdding(false);
                // The picker pages its options from the server, so the new row
                // is not in the page it is holding. Its label is not needed
                // here — an id it has no name for is one the picker fetches.
                void queryClient.invalidateQueries({ queryKey: ["compact", endpoint] });
                onCreated(String(saved.id));
              }}
            />,
            document.body,
          )
        : null}
    </div>
  );
}

function FieldInput({
  field,
  value,
  parent,
  onChange,
  editing = false,
  columns = 1,
  sectionStart = false,
}: {
  field: FieldDef;
  value: string;
  parent: string;
  onChange: (value: string) => void;
  editing?: boolean;
  columns?: 1 | 2;
  sectionStart?: boolean;
}) {
  const t = useTranslation();
  /**
   * The field's own id, unique to this instance of the form.
   *
   * Two of these forms are open at once whenever the + beside a relation field
   * is pressed, and an id is a claim that there is only one of something.
   * Sharing the field name across both made the second form's label point at
   * the first form's input: clicking "Name" over the province form put the
   * cursor in the city behind it.
   */
  const fieldId = `${field.name}${useId()}`;
  const options = useQuery({
    queryKey: ["options", field.name, parent],
    queryFn: ({ signal }) =>
      field.loadOptions?.(parent, signal) ?? Promise.resolve(field.options ?? []),
    enabled: field.type === "select" && (!field.dependsOn || parent !== ""),
    staleTime: 5 * 60_000,
  });

  /**
   * Whether the person has none of what this field asks for. A row already on
   * record with the field empty opens saying so; a new one starts assuming
   * they have one, which is the ordinary case.
   */
  const [absent, setAbsent] = useState(
    () => Boolean(field.absentToggle) && editing && value === "",
  );
  const locked = field.disabled || field.showOnly || (editing && field.lockOnEdit) || false;
  const disabled = locked || absent;
  const required = field.required && !absent;
  // A section heading groups the fields under it; the first one needs no gap
  // above it, the rest do, so the form reads as parts rather than a list.
  const heading = sectionStart ? (
    <h3
      className={cn(
        "text-eyebrow text-fg-subtle hairline-b pt-3 pb-2 first:pt-0",
        columns === 2 && "md:col-span-2",
      )}
    >
      {field.section}
    </h3>
  ) : null;

  let control: ReactNode;
  // A paragraph takes the whole row; a name or a number does not need to.
  let span = false;
  if (field.type === "fk") {
    const waiting = Boolean(field.dependsOn) && parent === "";
    const picker = (
      <FkSelect
        id={fieldId}
        endpoint={field.endpoint ?? ""}
        value={value}
        onChange={(next) => onChange(next)}
        params={
          field.dependsOn
            ? { ...field.params, [field.parentParam ?? field.dependsOn]: parent }
            : field.params
        }
        disabled={disabled || waiting}
        required={required}
        placeholder={
          waiting
            ? // The field it waits on, named as a person would: the filter
              // panel already drops the `_id`, and a form that says "choose a
              // province_id first" is the database talking.
              t("rp.chooseFirst").replace(
                "{parent}",
                field.dependsOn?.replace(/_id$/, "").replace(/_/g, " ") ?? "",
              )
            : t("rp.select").replace("{label}", field.label.toLowerCase())
        }
      />
    );
    control = field.create ? (
      <RelatedCreateField
        create={field.create}
        endpoint={field.endpoint ?? ""}
        disabled={disabled || waiting}
        onCreated={onChange}
      >
        {picker}
      </RelatedCreateField>
    ) : (
      picker
    );
  } else if (field.type === "select") {
    control = (
      <OptionSelect
        id={fieldId}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled || (Boolean(field.dependsOn) && parent === "")}
        placeholder={
          field.dependsOn && parent === ""
            ? // The field it waits on, named as a person would: the filter
              // panel already drops the `_id`, and a form that says "choose a
              // province_id first" is the database talking.
              t("rp.chooseFirst").replace(
                "{parent}",
                field.dependsOn?.replace(/_id$/, "").replace(/_/g, " ") ?? "",
              )
            : t("rp.select").replace("{label}", field.label.toLowerCase())
        }
        // An option's value may be a number on the way in; the control and the
        // form state both deal in strings.
        choices={(options.data ?? []).map((option) => ({
          value: String(option.value),
          label: option.label,
        }))}
      />
    );
  } else if (field.type === "boolean") {
    control = (
      <span className="flex h-10 items-center">
        <Toggle
          id={fieldId}
          disabled={disabled}
          checked={value === "true"}
          onChange={(event) => onChange(event.target.checked ? "true" : "false")}
        />
      </span>
    );
  } else if (field.type === "image") {
    control = (
      <label className="border-line-strong text-fg-muted text-subhead hover:border-accent hover:bg-accent-soft/40 rounded-card flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed p-4 transition-colors">
        {value ? (
          <img src={value} alt="" className="max-h-28 rounded-lg object-contain" />
        ) : null}
        <span>{value ? t("rp.changeImage") : t("rp.dropImage")}</span>
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void readImage(file, 800).then(onChange);
          }}
        />
      </label>
    );
  } else if (field.type === "file") {
    // Any document, kept as a data URL like the images; no resize, so the cap is the API's.
    control = (
      <label className="border-line-strong text-fg-muted text-subhead hover:border-accent hover:bg-accent-soft/40 rounded-card flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed p-4 transition-colors">
        <Upload className="size-5" aria-hidden />
        <span>
          {value
            ? t("rp.fileAttached").replace(
                "{kb}",
                String(Math.round((value.length * 3) / 4 / 1024)),
              )
            : t("rp.dropFile")}
        </span>
        <input
          type="file"
          className="sr-only"
          disabled={disabled}
          {...(field.accept ? { accept: field.accept } : {})}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            // Said before the read rather than after: a file over the cap is
            // refused by the API anyway, and encoding ten megabytes to find
            // that out is a minute of nothing happening.
            if (field.maxSizeMb && file.size > field.maxSizeMb * 1024 * 1024) {
              toast.error(t("rp.fileTooLarge").replace("{mb}", String(field.maxSizeMb)));
              return;
            }
            const reader = new FileReader();
            reader.onload = () => onChange(String(reader.result ?? ""));
            reader.readAsDataURL(file);
          }}
        />
      </label>
    );
  } else if (field.type === "date") {
    // One calendar on every platform, rather than whatever the operating
    // system draws for `<input type="date">`.
    control = (
      <DatePicker
        id={fieldId}
        disabled={disabled}
        required={required}
        value={value}
        onChange={onChange}
        // The bounds the screen declares. They used to stop at the field.
        min={typeof field.min === "string" ? field.min : undefined}
        max={typeof field.max === "string" ? field.max : undefined}
      />
    );
  } else if (field.type === "textarea") {
    span = columns === 2;
    control = (
      <Textarea
        id={fieldId}
        required={required}
        rows={3}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  } else if (field.uppercase) {
    // Capitals as they are typed, caret left where it was put — the same
    // control the enrolment form's name fields use.
    control = (
      <NameInput
        id={fieldId}
        aria-label={field.absentToggle ? field.label : undefined}
        required={required}
        disabled={disabled}
        value={value}
        onChange={onChange}
      />
    );
  } else {
    control = (
      <Input
        id={fieldId}
        type={field.type ?? "text"}
        // The label row carries the switch's words as well as the field's, so
        // the control says its own name rather than inheriting both.
        aria-label={field.absentToggle ? field.label : undefined}
        required={required}
        step={field.step}
        min={field.min}
        max={field.max}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <>
      {heading}
      <Field
        label={
          field.absentToggle ? (
            // A span rather than a nested <label>, which the <label> around it
            // would not allow; the switch names itself with `aria-label`, and
            // the control below names itself too, so neither takes the other's
            // words into its own accessible name.
            <span className="flex items-center justify-between gap-3">
              {/* The asterisk kept beside the words it belongs to: `Field`
                  puts its own after the whole label, which on a label this
                  wide drops it onto a line of its own under the name. */}
              <span>
                {field.label}
                {required ? (
                  <span aria-hidden className="text-danger ml-0.5">
                    *
                  </span>
                ) : null}
              </span>
              <span className="text-fg-muted text-footnote flex items-center gap-2 font-normal">
                {field.absentToggle}
                <Toggle
                  aria-label={field.absentToggle}
                  // Out of the tab order: Tab walks a form field to field, and
                  // a switch sitting between two of them stops the typing to
                  // ask a question most people are not answering. It is
                  // reached with the pointer, or with Shift+Tab off the field
                  // it governs. See `absentToggle`.
                  tabIndex={-1}
                  checked={absent}
                  disabled={locked}
                  onChange={(event) => {
                    setAbsent(event.target.checked);
                    // What they do not have, they do not keep half-typed: a
                    // name already entered goes when the switch says there is
                    // none, rather than sitting greyed out behind it.
                    if (event.target.checked) onChange("");
                  }}
                />
              </span>
            </span>
          ) : (
            field.label
          )
        }
        htmlFor={fieldId}
        required={field.absentToggle ? undefined : required}
        className={cn(span && "md:col-span-2")}
      >
        {control}
      </Field>
    </>
  );
}
